import { APP_URL } from "@/lib/shareOrigin";
import type {
  CandidateDraftRefresh,
  IStaffingAdminService,
} from "@/services/interfaces/IStaffingAdminService";
import type {
  AdminLead,
  AdminLeadFilters,
  LeadCreateInput,
  LeadImportOutcome,
  LeadImportReport,
  LeadImportRow,
  LeadImportRowResult,
  LeadSourceCandidate,
  LeadUpdateInput,
} from "@/lib/leadTypes";
import type {
  PaginatedResult,
  AdminQuestCandidate,
  CandidateAiAssessment,
  AdminCandidateFilters,
  AdminSettableCandidateStatus,
  CandidateSortKey,
  SortDir,
  HeadhunterRun,
  HeadhunterRunFilters,
  HeadhunterHealth,
  HeadhunterLogEvent,
  HeadhunterLogFilters,
  StoredTestbedCandidate,
  TestbedCandidateFilters,
  HeadhunterRunStats,
  HeadhunterMetricsFilters,
  HeadhunterRunMetrics,
  HeadhunterCandidateFunnel,
  HeadhunterDropBreakdown,
  PendingQuest,
  PendingQuestFilters,
  OutreachDraft,
  OutreachChannel,
  OutreachDraftFilters,
  DraftOutreachResult,
  ReenrichCandidateResult,
  TestbedOutreachResult,
  SendOutreachDraftResult,
  RunHeadhunterResult,
  SearchTestbedParams,
  SearchTestbedResult,
  ParsedSearchQuery,
  OtherQuestMatch,
  HeadhunterRunKind,
  SearchTargetType,
  CandidateUpdateInput,
  QuestCandidateCreateInput,
} from "@/lib/types";
import { CANDIDATE_EMAIL_PATTERN } from "@/lib/staffingTypes";
// Values, not types: the mock stands in for the database in mock mode, so its
// refusals must apply the SAME rules the triggers do — including normalizing a
// contact before comparing it, which is what `leads_normalize_biu` does for
// every real writer.
import {
  countLeadImport,
  isLeadE164,
  isLeadEmailPlausible,
  LEAD_IMPORT_MAX_ROWS,
  normalizeLeadPhone,
} from "@/lib/leadTypes";
import { ADMIN_PAGE_SIZE } from "@/lib/constants";

const MOCK_DELAY_MS = 400;
const DAY_MS = 86_400_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * DAY_MS).toISOString();
}

/**
 * Sort candidates the way admin_list_quest_candidates does, so mock-admin and the
 * paginated table behave identically to production. Score/created nulls always
 * sort last (independent of direction), then a created-desc tiebreaker keeps the
 * order stable. Does not mutate the input.
 */
function sortCandidates(
  rows: AdminQuestCandidate[],
  key: CandidateSortKey,
  dir: SortDir,
): AdminQuestCandidate[] {
  const mul = dir === "asc" ? 1 : -1;
  // NULL sorts last for every key/direction, matching the RPC's `NULLS LAST`.
  // Only a real null counts as null — SQL NULLS LAST never treats an empty string
  // as NULL, so the mock must not either.
  const nullsLast = (a: unknown, b: unknown): number | null => {
    const an = a == null;
    const bn = b == null;
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    return null;
  };
  return [...rows].sort((a, b) => {
    let cmp: number;
    if (key === "name") {
      const n = nullsLast(a.fullName, b.fullName);
      cmp = n ?? (a.fullName.localeCompare(b.fullName) * mul);
    } else if (key === "created") {
      const n = nullsLast(a.createdAt, b.createdAt);
      cmp = n ?? (a.createdAt!.localeCompare(b.createdAt!) * mul);
    } else {
      const n = nullsLast(a.score, b.score);
      cmp = n ?? ((a.score! - b.score!) * mul);
    }
    if (cmp !== 0) return cmp;
    // Stable tiebreaker: newest first (matches the RPC's trailing created_at DESC).
    return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
  });
}

// DEV-359: mock stand-in for quests.chain_id. Candidates and drafts derive
// their network from the parent quest (not stored on the row), so map each
// fixture quest id to a network. Spread across both Amoy (80002) and Polygon
// (137) so the top-level network toggle is exercisable in mock-admin mode.
const MOCK_QUEST_CHAIN: Record<string, number> = {
  "mock-quest-001": 80002,
  "mock-quest-002": 137,
  "mock-quest-003": 80002,
  "admin-quest-001": 80002,
  "admin-quest-002": 137,
};
function mockChainForQuest(questId: string): number {
  return MOCK_QUEST_CHAIN[questId] ?? 80002;
}

/**
 * DEV-809 house format, in fixture form.
 *
 * These drafts are what an admin reviews in mock-admin mode, so they have to
 * carry the SAME shape the worker actually sends — a facts-first `·` subject
 * (pay · what · where · when), the hero card, the plain-text repeat of the same
 * facts, a CTA to the specific quest, and the "weXare never asks you for money"
 * close. Fixtures written before that standard made the Outbox look like it was
 * sending pre-DEV-809 mail when the templates had already moved on.
 *
 * The real assembly lives in services/headhunter-worker/src/outreach — this is
 * a hand-written stand-in for it, not a second implementation.
 */
interface MockEmailFields {
  greeting: string;
  intro: string;
  /**
   * The quest the draft is about. Only the hero image needs it: the art is
   * keyed by quest id and resolved by /api/quest-art, exactly as the worker's
   * `artUrlFor` does it.
   */
  questId: string;
  title: string;
  business: string;
  pay: string;
  when: string;
  place: string;
  verified: string;
  ctaLabel: string;
  ctaUrl: string;
  noMoney: string;
  signoff: string;
  who: string;
  source: string;
  optOut: string;
  /**
   * Cold-contact compliance tail. Present for EXTERNAL drafts only: the
   * one-click unsubscribe URL is a condition of approval (the guard in
   * reviewOutreachDraft refuses without it), and an internal member email to a
   * consenting member carries neither it nor the source notice.
   */
  optOutUrl?: string;
  /**
   * Explicit app origin for art URLs. Required when rendering in SSR / Node
   * where `window.location.origin` is unavailable.
   */
  appOrigin?: string;
}

/**
 * The same URL the worker's `artUrlFor` builds. Absolute, because the preview
 * renders inside a `sandbox=""` iframe on an opaque origin, where a relative
 * src is not reliably resolvable.
 *
 * Accepts an explicit `origin` so SSR / Node callers can supply one; falls
 * back to `window.location.origin` when running in the browser.
 */
function mockArtUrl(questId: string, origin?: string): string {
  const base = origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/api/quest-art?q=${encodeURIComponent(questId)}`;
}

function mockOutreachHtml(f: MockEmailFields): string {
  const facts = [f.business, f.pay, f.when, f.place, f.verified];
  return [
    '<div style="font-family:system-ui,sans-serif;max-width:600px;color:#1a1a1a">',
    `<p>${f.greeting}</p>`,
    `<p>${f.intro}</p>`,
    // Hero card: the job title and the four facts, repeated as plain text below
    // so the mail still works with images blocked.
    '<table role="presentation" width="100%" style="background:#eef0ff;border-radius:12px;overflow:hidden;margin:16px 0">',
    // The hero image, as the real template has it (emailShell.ts): the first
    // row of the card, above the title. Pointed at the live /api/quest-art so
    // this preview exercises the actual redirect rather than a stand-in — a
    // broken hero card in the outbox now looks broken here too, which is the
    // only reason this row is worth carrying in mock data.
    `<tr><td><img src="${mockArtUrl(f.questId, f.appOrigin)}" alt="${f.title}" width="600" style="display:block;width:100%;max-width:600px;height:auto"></td></tr>`,
    `<tr><td style="padding:20px"><h2 style="margin:0 0 12px">${f.title}</h2>`,
    facts.map((line) => `<p style="margin:4px 0;font-size:14px">${line}</p>`).join(""),
    "</td></tr></table>",
    '<blockquote style="border-left:3px solid #6C5CE7;padding-left:12px;font-size:14px;color:#404040">',
    facts.map((line) => `<div>${line}</div>`).join(""),
    "</blockquote>",
    `<p><a href="${f.ctaUrl}" style="display:inline-block;background:#6C5CE7;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none">${f.ctaLabel}</a></p>`,
    // Directly under the CTA, always — the anti-scam close.
    `<p style="font-size:13px;color:#525252">${f.noMoney}</p>`,
    ...(f.optOutUrl
      ? [
          `<p style="font-size:12px;color:#737373">${f.who}</p>`,
          `<p style="font-size:12px;color:#737373">${f.source}</p>`,
          `<p style="font-size:12px;color:#737373">${f.optOut} <a href="${f.optOutUrl}">${f.optOutUrl}</a></p>`,
        ]
      : []),
    `<p style="font-size:13px;color:#737373">${f.signoff}<br>${ADDRESS}</p>`,
    "</div>",
  ].join("");
}

const ADDRESS = "weXare Inc., 1111B S Governors Ave, Dover, DE 19904, USA";

const EN = {
  verified: "Business details confirmed by weXare",
  who: "We're weXare, a platform that connects people to paid tasks (\"quests\").",
  source:
    "How we found you: we located your public professional profile (name, role, skills) through a people-search provider. We used it once, to invite you — we never sell or share your contact details.",
  optOut: "Don't want to hear from us? Unsubscribe (one click):",
  noMoney:
    "weXare never asks you for money — no deposit, no training fee, nothing to pay to start. There is nothing to sign up for and nothing to commit to.",
  signoff: "— The weXare team",
};
const ES = {
  verified: "Datos del negocio verificados por weXare",
  who: "Somos weXare, una plataforma que conecta a personas con tareas pagas (\"misiones\").",
  source:
    "Cómo te encontramos: ubicamos tu perfil profesional público (nombre, rol, habilidades) mediante un proveedor de búsqueda de personas. Lo usamos una sola vez, para invitarte — nunca vendemos ni compartimos tus datos de contacto.",
  optOut: "¿No querés recibir estos correos? Cancelar suscripción (un clic):",
  noMoney:
    "weXare nunca te pide dinero — sin depósito, sin cuota de formación, nada que pagar para empezar. No hay nada a lo que apuntarse, ni nada a lo que comprometerse.",
  signoff: "— El equipo de weXare",
};

const GATEWAY = {
  questId: "admin-quest-001",
  title: "Build Payment Gateway",
  business: "weXare · Platform team, remote",
  pay: "Pay: $1,200 for the whole quest (all its tasks), within 3 days of delivery",
  when: "Monday 15 September, 9am",
  place: "Where: remote",
};
const LANDING = {
  questId: "admin-quest-002",
  title: "Design Landing Page",
  business: "Estudio Ruzafa · Agencia de diseño, seis personas, Buenos Aires",
  pay: "Pago: $640 por toda la misión (todas sus tareas), dentro de 3 días tras entregar",
  when: "Jueves 18 de septiembre, 10am",
  place: "Dónde: Buenos Aires",
};

/**
 * `‹creator› would like you on this job` — mirrors buildSubject() in the worker.
 *
 * Person-first and money-free: a currency amount in the subject is what put
 * these mails in the spam folder. DEV-855 also took the quest TITLE out — it
 * moved to the preheader — so the row stays inside what a phone shows and reads
 * as one person writing to another rather than as a listing feed.
 */
const GATEWAY_SUBJECT = "The weXare platform team would like you on this job";
const LANDING_SUBJECT = "Estudio Ruzafa te quiere en este trabajo";

function mockOutreachText(f: MockEmailFields): string {
  return [
    f.greeting,
    "",
    f.intro,
    "",
    f.title,
    f.business,
    f.pay,
    f.when,
    f.place,
    f.verified,
    "",
    `${f.ctaLabel}: ${f.ctaUrl}`,
    "",
    f.noMoney,
    ...(f.optOutUrl ? ["", f.who, "", f.source, "", `${f.optOut} ${f.optOutUrl}`] : []),
    "",
    f.signoff,
    ADDRESS,
  ].join("\n");
}

const GATEWAY_ANA = {
  greeting: "Hi Ana,",
  intro: "We're weXare — we found you online, and we think this job could be a fit:",
  ...GATEWAY,
  ...EN,
  ctaLabel: "Sign up and apply",
  ctaUrl: `${APP_URL}/r#example-token`,
  optOutUrl: `${APP_URL}/unsubscribe#example-token`,
};
const LANDING_CARLOS = {
  greeting: "Hola Carlos:",
  intro: "Somos weXare — te encontramos online y creemos que este trabajo puede encajar contigo:",
  ...LANDING,
  ...ES,
  ctaLabel: "Registrarte y postularte",
  ctaUrl: `${APP_URL}/r#example-token-2`,
  optOutUrl: `${APP_URL}/unsubscribe#example-token-2`,
};
const GATEWAY_BOB = {
  greeting: "Hi Bob,",
  intro: "I'm Rodri, an AI agent from weXare. We think this job could be a fit for you:",
  ...GATEWAY,
  ...EN,
  ctaLabel: "Open weXare",
  ctaUrl: `${APP_URL}/quest/admin-quest-001`,
};
const GATEWAY_DIANA = {
  greeting: "Hi Diana,",
  intro: "We're weXare — we found you online, and we think this job could be a fit:",
  ...GATEWAY,
  ...EN,
  ctaLabel: "Sign up and apply",
  ctaUrl: `${APP_URL}/r#example-token-4`,
  optOutUrl: `${APP_URL}/unsubscribe#example-token-4`,
};

/** Monotonic, so a create after a delete cannot reuse a live row's id. */
let mockManualLeadSeq = 0;

/** Monotonic, so a manual candidate create after a delete cannot reuse a live row's id. */
let mockManualCandidateSeq = 0;

/**
 * DEV-819: the contacts mock mode treats as opted out.
 *
 * In production this lives in `contact_suppressions` and is enforced by the
 * `leads_zz_reject_suppressed_biu` trigger, which no mock has. Without a stand-in
 * here, the one refusal DEV-819 names alongside the duplicate — "this person
 * opted out" — is the only one mock mode could not demonstrate, and the
 * adapter's comment claiming otherwise was simply false.
 *
 * Hashes are not modelled: the real table stores sha256 of the normalized
 * contact, and reproducing that here would buy nothing the demo needs.
 */
const MOCK_SUPPRESSED_CONTACTS = {
  emails: ["opted-out@example.com"],
  phones: ["+595981000999"],
};

/**
 * DEV-819: a lead refusal, carrying the SQLSTATE the RPC would have raised.
 *
 * The Supabase adapter preserves the PostgrestError as `cause`, so a caller can
 * branch on `cause.code` instead of string-matching English prose. This mock
 * must therefore carry the same codes, or that branch silently takes a different
 * path in mock mode — the exact class of Mock/Supabase drift that let the
 * existing-user refusal go missing here in the first place.
 *
 * `23505` is unique_violation (a duplicate contact, or the create/delete race);
 * `23514` is check_violation (every refusal the RPC raises with that ERRCODE,
 * plus the suppression trigger's). Those two do not distinguish, say, a blank
 * name from an opted-out contact — the RPC does not either, which is why the
 * message text is still what an admin reads.
 */
/**
 * DEV-822 — the mock mirror of the RPC's `LEFT JOIN LATERAL`: which lead, if any,
 * holds this candidate's person.
 *
 * THE PRECEDENCE IS THE POINT, and it is the same one three other places already
 * use — `admin_create_lead_from_candidate`'s conflict resolution, the SQL in
 * 20260909170000, and `pickLead` in `_shared/leadClaim.ts`: source candidate
 * first, then email, then phone. The `??` chain IS that ordering; reordering it
 * would make mock mode disagree with the server about which lead holds someone,
 * and the badge is the surface where that disagreement would be invisible.
 *
 * NORMALIZED BEFORE COMPARING, for the reason `createLeadManual` states above:
 * `MOCK_LEADS` holds what the trigger would have stored, while a candidate's
 * email and phone hold whatever a vendor returned. Comparing raw values would
 * miss every lead differing only in case or punctuation — the same bug the SQL
 * avoids by calling `leads_normalize_*` rather than comparing columns directly.
 */
function resolveCandidateLeadState(
  candidate: AdminQuestCandidate,
): Pick<AdminQuestCandidate, "leadId" | "leadClaimedAt"> {
  const email = candidate.email?.trim().toLowerCase() || null;
  const phone = candidate.phone ? normalizeLeadPhone(candidate.phone) : null;
  const match =
    MOCK_LEADS.find((l) => l.sourceCandidateId === candidate.id) ??
    (email ? MOCK_LEADS.find((l) => l.email === email) : undefined) ??
    (phone ? MOCK_LEADS.find((l) => l.phone === phone) : undefined);
  return { leadId: match?.id ?? null, leadClaimedAt: match?.claimedAt ?? null };
}

function leadRefusal(code: "23505" | "23514", message: string): Error {
  const err = new Error(message);
  // ASSIGNED, not passed to the constructor: `new Error(msg, { cause })` is
  // ES2022 and this project targets ES2020, so the two-argument form does not
  // typecheck (TS2554). The property is what a caller reads either way.
  (err as Error & { cause?: unknown }).cause = { code, message };
  return err;
}

/**
 * DEV-819: the emails mock mode treats as already belonging to a registered
 * user.
 *
 * In production this is `public.profiles`, probed by admin_create_lead_manual
 * before the duplicate checks. A lead only pre-fills a profile at FIRST login,
 * so a lead for someone who already has one could never be claimed — the RPC
 * refuses it rather than write dead data behind a toast promising a pre-fill.
 * No mock has a profiles table, and without this stand-in that refusal is the
 * one mock mode cannot demonstrate, exactly as the suppression list above was
 * added for.
 *
 * `hunter@wexare.dev` is MockUserService's own signed-in user, so the refusal
 * here means the same thing it means in production rather than naming an
 * address nothing else in mock mode knows about.
 *
 * EMAIL ONLY, mirroring the RPC's documented limit: `profiles` has no phone
 * column, so a phone-only lead for an existing user is still created.
 */
const MOCK_EXISTING_USER_EMAILS = ["hunter@wexare.dev"];

// Mutable so edit/approve/reject reflect across calls within a dev session.
/**
 * DEV-817 lead fixtures, chosen so mock-admin mode can drive every state the
 * Leads view branches on without a database: one CLAIMED lead (read-only — no
 * edit, no delete), one candidate-sourced lead (has provenance to link back to),
 * one hand-entered lead (no provenance at all), and one phone-only lead (the
 * contact shape the E.164 rule exists for).
 *
 * `let`, not `const`: deleteLead reassigns it, so a mock-mode delete actually
 * removes the row and the list reflects it.
 */
let MOCK_LEADS: AdminLead[] = [
  {
    id: "mock-lead-1",
    email: "ada@example.com",
    phone: null,
    displayName: "Ada Sourced",
    locationCity: "Asuncion",
    locationCountry: "Paraguay",
    skills: ["wiring", "conduit"],
    employmentTitle: "Electrician",
    source: "candidate",
    sourceCandidateId: "mock-cand-1",
    candidateIdentityId: "mock-identity-1",
    createdBy: "did:privy:mock-admin",
    notes: "Sourced from the June run.",
    claimedBy: null,
    claimedAt: null,
    createdAt: "2026-09-01T10:00:00.000Z",
    updatedAt: "2026-09-01T10:00:00.000Z",
  },
  {
    id: "mock-lead-2",
    email: "bruno@example.com",
    phone: "+595981123456",
    displayName: "Bruno Claimed",
    locationCity: "Ciudad del Este",
    locationCountry: "Paraguay",
    skills: ["plumbing"],
    employmentTitle: "Plumber",
    source: "outreach",
    sourceCandidateId: null,
    candidateIdentityId: null,
    createdBy: "did:privy:mock-admin",
    notes: null,
    // Claimed: the person logged in and their profile was pre-filled. Read-only.
    claimedBy: "did:privy:mock-bruno",
    claimedAt: "2026-09-05T08:30:00.000Z",
    createdAt: "2026-08-20T09:00:00.000Z",
    updatedAt: "2026-09-05T08:30:00.000Z",
  },
  {
    id: "mock-lead-3",
    email: null,
    phone: "+595971555000",
    displayName: "Carla Phoneonly",
    locationCity: null,
    locationCountry: "Paraguay",
    skills: [],
    employmentTitle: null,
    source: "manual",
    sourceCandidateId: null,
    candidateIdentityId: null,
    createdBy: "did:privy:mock-admin",
    notes: "Walk-in; phone only.",
    claimedBy: null,
    claimedAt: null,
    createdAt: "2026-09-06T14:00:00.000Z",
    updatedAt: "2026-09-06T14:00:00.000Z",
  },
  {
    id: "mock-lead-4",
    email: "dora@example.com",
    phone: null,
    displayName: "Dora Imported",
    locationCity: "Encarnacion",
    locationCountry: "Paraguay",
    skills: ["welding"],
    employmentTitle: "Welder",
    source: "csv",
    sourceCandidateId: null,
    candidateIdentityId: null,
    createdBy: null,
    notes: null,
    claimedBy: null,
    claimedAt: null,
    createdAt: "2026-09-07T11:00:00.000Z",
    updatedAt: "2026-09-07T11:00:00.000Z",
  },
];

const MOCK_OUTREACH_DRAFTS: OutreachDraft[] = [
  {
    id: "draft-001", candidateId: "mock-cand-ext-1", questId: "admin-quest-001",
    questTitle: "Build Payment Gateway", email: "ana.dev@example.com",
    fullName: "Ana Dev", locationCountry: "Paraguay",
    subject: GATEWAY_SUBJECT,
    body: mockOutreachText(GATEWAY_ANA),
    status: "pending_review", kind: "external", failureReason: null,
    reviewedBy: null, reviewedAt: null, sentAt: null,
    createdAt: daysAgo(1), updatedAt: daysAgo(1),
    candidateScore: 0.91, candidateSkills: ["Stripe", "Node.js", "TypeScript"],
    candidateLinkedinUrl: "https://linkedin.com/in/ana-dev-example",
    candidateSourceUrl: null, candidateEmploymentTitle: "Full-Stack Engineer",
    candidateLocationCity: "Asunción",
    candidateDismissalReason: null, candidateDismissalNote: null,
    candidateAiProfile: "Ana has 4+ years of full-stack experience with a strong focus on payment systems and Stripe integrations. Her Node.js and TypeScript background directly matches the technical requirements of this quest, and her portfolio shows two prior payment gateway implementations delivered on time.",
    candidateSkillsFromQuery: false,
    intro: GATEWAY_ANA.intro, bodyHtml: mockOutreachHtml(GATEWAY_ANA), bodyOverride: false,
    channel: "email", phone: null,
  },
  {
    id: "draft-002", candidateId: "mock-cand-ext-2", questId: "admin-quest-002",
    questTitle: "Design Landing Page", email: "carlos.ux@example.com",
    fullName: "Carlos UX", locationCountry: "Argentina",
    subject: LANDING_SUBJECT,
    body: mockOutreachText(LANDING_CARLOS),
    status: "approved", kind: "external", failureReason: null,
    reviewedBy: "mock-admin", reviewedAt: daysAgo(0), sentAt: null,
    createdAt: daysAgo(2), updatedAt: daysAgo(0),
    candidateScore: 0.63, candidateSkills: ["Figma", "CSS", "React"],
    candidateLinkedinUrl: null,
    candidateSourceUrl: "https://behance.net/carlos-ux-example",
    candidateEmploymentTitle: "UX Designer", candidateLocationCity: "Buenos Aires",
    candidateDismissalReason: null, candidateDismissalNote: null,
    candidateAiProfile: null,
    candidateSkillsFromQuery: false,
    intro: LANDING_CARLOS.intro, bodyHtml: mockOutreachHtml(LANDING_CARLOS), bodyOverride: false,
    channel: "email", phone: null,
  },
  {
    id: "draft-003", candidateId: "mock-cand-int-1", questId: "admin-quest-001",
    questTitle: "Build Payment Gateway", email: "bob.member@example.com",
    fullName: "Bob Member", locationCountry: "Peru",
    subject: GATEWAY_SUBJECT,
    body: mockOutreachText(GATEWAY_BOB),
    status: "pending_review", kind: "internal", failureReason: null,
    reviewedBy: null, reviewedAt: null, sentAt: null,
    createdAt: daysAgo(0), updatedAt: daysAgo(0),
    candidateScore: null, candidateSkills: null, candidateLinkedinUrl: null,
    candidateSourceUrl: null, candidateEmploymentTitle: null, candidateLocationCity: null,
    candidateDismissalReason: null, candidateDismissalNote: null,
    candidateAiProfile: null,
    candidateSkillsFromQuery: false,
    intro: GATEWAY_BOB.intro, bodyHtml: mockOutreachHtml(GATEWAY_BOB), bodyOverride: false,
    channel: "email", phone: null,
  },
  {
    id: "draft-004", candidateId: "mock-cand-int-2", questId: "admin-quest-001",
    questTitle: "Build Payment Gateway", email: "diana.rejected@example.com",
    fullName: "Diana Rejected", locationCountry: "Brazil",
    subject: GATEWAY_SUBJECT,
    body: mockOutreachText(GATEWAY_DIANA),
    status: "rejected", kind: "external", failureReason: null,
    reviewedBy: "mock-admin", reviewedAt: daysAgo(1), sentAt: null,
    createdAt: daysAgo(2), updatedAt: daysAgo(1),
    candidateScore: 0.44, candidateSkills: ["Figma", "Illustrator"],
    candidateLinkedinUrl: null, candidateSourceUrl: null,
    candidateEmploymentTitle: "Graphic Designer", candidateLocationCity: "São Paulo",
    candidateDismissalReason: "seniority", candidateDismissalNote: "Looking for senior-level experience",
    candidateAiProfile: null,
    candidateSkillsFromQuery: false,
    // Left without an intro on purpose: a draft whose opening line was never
    // generated is a real state, and this is the fixture that exercises it.
    intro: null, bodyHtml: mockOutreachHtml(GATEWAY_DIANA), bodyOverride: false,
    channel: "email", phone: null,
  },
];

export class MockStaffingService implements IStaffingAdminService {
  // ─── Outreach review (DEV-351 R-B) ────────────────────
  async listOutreachDrafts(filters: OutreachDraftFilters): Promise<OutreachDraft[]> {
    await delay(MOCK_DELAY_MS);
    let rows = filters.status
      ? MOCK_OUTREACH_DRAFTS.filter((d) => d.status === filters.status)
      : MOCK_OUTREACH_DRAFTS;
    if (filters.chainId != null) {
      rows = rows.filter((d) => mockChainForQuest(d.questId) === filters.chainId);
    }
    // Mirror the real RPC: join live dismissal state from the candidate row.
    return rows.map((d) => {
      const cand = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === d.candidateId);
      return {
        ...d,
        candidateDismissalReason: cand?.dismissalReason ?? d.candidateDismissalReason,
        candidateDismissalNote: cand?.dismissalNote ?? d.candidateDismissalNote,
        candidateAiProfile: cand?.aiProfile ?? d.candidateAiProfile,
        // DEV-503: the real RPC reads this straight off the candidate row, so the
        // mock must join it too — otherwise mock-admin mode can show a draft whose
        // provenance disagrees with the candidate it was drafted from.
        candidateSkillsFromQuery: cand?.skillsFromQuery ?? d.candidateSkillsFromQuery,
      };
    });
  }

  /**
   * DEV-376 — the structured save, mirroring the worker's editDraft.ts: the
   * override latch and the re-queue are reproduced here so mock-admin mode
   * cannot show an edit flow the real backend would refuse.
   */
  async saveOutreachDraftFields(
    id: string,
    patch: { subject?: string; intro?: string; clearOverride?: boolean },
  ): Promise<
    | { status: "saved"; subject: string; body: string; bodyHtml: string }
    | { status: "locked"; reason: "sent" | "override" }
  > {
    await delay(MOCK_DELAY_MS);
    const d = MOCK_OUTREACH_DRAFTS.find((x) => x.id === id);
    if (!d) return { status: "locked", reason: "sent" };
    if (d.status !== "pending_review" && d.status !== "approved") {
      return { status: "locked", reason: "sent" };
    }
    const reverting = patch.intro === undefined && patch.subject === undefined;
    if (d.bodyOverride && !reverting && !patch.clearOverride) {
      return { status: "locked", reason: "override" };
    }

    const intro = patch.intro?.trim() || d.intro || "";
    // The real render lives in the worker; the mock only needs a body that
    // visibly follows the intro so the preview is exercised.
    // The real render always emits the compliance footer; the mock must too, or
    // a save here produces a body this service's own approve guard would reject.
    const footer =
      d.kind === "external"
        ? `\n\nSign up and apply: ${APP_URL}/r#${d.id}\n\nUnsubscribe: ${APP_URL}/unsubscribe#${d.id}\n\n— The Wexare team`
        : "\n\n— The Wexare team";
    const body = `${intro}\n\n${d.questTitle ?? ""}${footer}`.trim();
    const bodyHtml = `<table><tr><td>${intro}</td></tr><tr><td>${footer.trim()}</td></tr></table>`;
    d.subject = patch.subject?.trim() || d.subject;
    d.intro = intro;
    d.body = body;
    d.bodyHtml = bodyHtml;
    d.bodyOverride = false;
    d.status = "pending_review";
    d.reviewedBy = null;
    d.reviewedAt = null;
    d.updatedAt = new Date().toISOString();
    return { status: "saved", subject: d.subject, body, bodyHtml };
  }

  /** DEV-376 — the hand-edit save: verbatim bytes, and the latch goes on. */
  async saveOutreachDraftRaw(
    id: string,
    next: { subject: string; body: string; bodyHtml: string; expectedUpdatedAt: string },
  ): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const d = MOCK_OUTREACH_DRAFTS.find((x) => x.id === id);
    // Mirror the RPC's optimistic-concurrency refusal, error text included.
    if (d && next.expectedUpdatedAt && d.updatedAt !== next.expectedUpdatedAt) {
      throw new Error("draft changed since you opened it");
    }
    if (d) {
      d.subject = next.subject;
      d.body = next.body;
      d.bodyHtml = next.bodyHtml;
      d.bodyOverride = true;
      d.status = "pending_review";
      d.reviewedBy = null;
      d.reviewedAt = null;
      d.updatedAt = new Date().toISOString();
    }
  }

  async setOutreachDraftContact(
    id: string,
    contact: { channel: OutreachChannel; email: string | null; phone: string | null },
  ): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const d = MOCK_OUTREACH_DRAFTS.find((x) => x.id === id);
    if (!d) return;
    // Mirror admin_set_outreach_draft_contact's refusals and, crucially, its
    // re-queue. A mock that let an approved draft keep its approval through a
    // recipient change would make the UI look correct against a backend that
    // refuses — the exact bug this mock exists to catch.
    if (d.status !== "pending_review" && d.status !== "approved") {
      throw new Error(`draft is not editable in status ${d.status}`);
    }
    if (contact.channel === "email" && !contact.email) {
      throw new Error("a valid email is required for channel email");
    }
    if (contact.channel !== "email" && !/^\+[1-9][0-9]{6,14}$/.test(contact.phone ?? "")) {
      throw new Error(`a valid E.164 phone is required for channel ${contact.channel}`);
    }
    d.channel = contact.channel;
    d.email = contact.email;
    d.phone = contact.phone;
    d.status = "pending_review";
    d.reviewedBy = null;
    d.reviewedAt = null;
    d.updatedAt = new Date().toISOString();
  }

  async reviewOutreachDraft(
    id: string,
    decision: "approved" | "rejected",
  ): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const d = MOCK_OUTREACH_DRAFTS.find((x) => x.id === id);
    // DEV-376: mirror admin_review_outreach_draft's compliance guard, including
    // its error text — useAdminOutreach's complianceGuardMessage matches on it.
    // Without this the mock approves cold email the real backend refuses, and
    // mock-admin mode cannot exercise the refusal path at all.
    if (d && decision === "approved" && d.kind === "external") {
      const parts = [d.body, d.bodyHtml?.trim() ? d.bodyHtml : ""].filter(Boolean) as string[];
      if (parts.some((part) => !part.includes("/r#"))) {
        throw new Error("cannot approve: the apply link is missing from the message");
      }
      if (parts.some((part) => !part.includes("/unsubscribe#"))) {
        throw new Error("cannot approve: the one-click unsubscribe link is missing from the message");
      }
    }
    if (d) {
      d.status = decision;
      d.reviewedBy = "mock-admin";
      d.reviewedAt = new Date().toISOString();
      d.updatedAt = d.reviewedAt;
    }
  }

  async draftOutreachForCandidate(candidateId: string): Promise<DraftOutreachResult> {
    await delay(MOCK_DELAY_MS);
    // Idempotent: if a live (non-terminal) draft already exists, report skip.
    const existing = MOCK_OUTREACH_DRAFTS.find(
      (d) => d.candidateId === candidateId && (d.status === "pending_review" || d.status === "approved"),
    );
    if (existing) return { status: "skipped", reason: "already_drafted" };

    const now = new Date().toISOString();
    const draft: OutreachDraft = {
      id: `draft-${candidateId}`,
      candidateId,
      questId: "admin-quest-001",
      questTitle: "Mock quest",
      email: "mock@example.com",
      channel: "email",
      phone: null,
      fullName: "Mock Candidate",
      locationCountry: "US",
      subject: "A Wexare quest matches your profile",
      body: "Hi — a quest matches your profile. https://app/r#mock https://app/unsubscribe#mock",
      status: "pending_review",
      // On-demand drafting only exists for external (cold-email) candidates.
      kind: "external",
      failureReason: null,
      reviewedBy: null,
      reviewedAt: null,
      sentAt: null,
      createdAt: now,
      updatedAt: now,
      candidateScore: null, candidateSkills: null, candidateLinkedinUrl: null,
      candidateSourceUrl: null, candidateEmploymentTitle: null, candidateLocationCity: null,
      candidateDismissalReason: null, candidateDismissalNote: null,
      candidateAiProfile: null,
      candidateSkillsFromQuery: false,
      // DEV-376: a freshly drafted row is generated, never hand-edited.
      intro: null,
      bodyHtml: null,
      bodyOverride: false,
    };
    MOCK_OUTREACH_DRAFTS.push(draft);
    return { status: "created", draftId: draft.id };
  }

  /**
   * DEV-822: this used to `return \`mock-lead-${candidateId}\`` and write
   * nothing.
   *
   * That was harmless while no surface read lead state back — and became a real
   * gap the moment one did. DEV-822's acceptance criterion is that creating a
   * lead VISIBLY CHANGES THE CANDIDATE ROW without a manual reload, and mock mode
   * (VITE_MOCK_ROLE=admin) is where this admin flow is actually clicked through.
   * A stub that invents an id the store has never heard of means the refetch
   * still finds no lead, the badge never appears, and the feature looks broken
   * exactly where it is verified by hand.
   *
   * So it inserts, and it refuses what the RPC refuses, in the RPC's own order:
   * unknown candidate, not external, already converted, no usable contact, no
   * name — then the idempotent contact conflict. Same reasoning as
   * `createLeadManual`: mock mode is not trying to be a database, but where a
   * refusal is the observable behaviour, a mock that always succeeds lets the
   * real thing regress unnoticed.
   */
  async createLeadFromCandidate(candidateId: string): Promise<string> {
    await delay(MOCK_DELAY_MS);
    const c = MOCK_HEADHUNTER_CANDIDATES.find((x) => x.id === candidateId);
    if (!c) {
      throw leadRefusal("23514", `candidate ${candidateId} not found`);
    }
    // An internal member already HAS a profile; a lead would be a second,
    // unclaimable copy of them.
    if (c.source !== "external") {
      throw leadRefusal("23514", `only external candidates become leads (candidate ${candidateId} is ${c.source})`);
    }
    if (c.convertedUserId != null) {
      throw leadRefusal("23514", `candidate ${candidateId} already converted`);
    }

    // Preflight, exactly as the RPC does it: an unusable phone is DROPPED rather
    // than fatal, so a candidate whose email alone would make a valid lead is not
    // sunk by a junk phone number.
    const email = c.email?.trim().toLowerCase() || null;
    const phone = c.phone ? normalizeLeadPhone(c.phone) : null;
    if (!email && !phone) {
      throw leadRefusal("23514", `candidate ${candidateId} has no contact; a lead without one can never be claimed`);
    }
    const name = c.fullName?.trim() || null;
    if (!name) {
      throw leadRefusal(
        "23514",
        `candidate ${candidateId} has no name; a lead would overwrite the claimer's own login name with a placeholder`,
      );
    }

    // Idempotent on the CONTACT — email first, then phone, never one OR-ed
    // lookup. This is the behaviour DEV-822's whole design turns on: a lead
    // already holding this person is RETURNED, not duplicated, and its
    // sourceCandidateId is deliberately left alone (provenance is a record of
    // what happened, not a field a later click may rewrite).
    const existing =
      (email ? MOCK_LEADS.find((l) => l.email === email) : undefined) ??
      (phone ? MOCK_LEADS.find((l) => l.phone === phone) : undefined);
    if (existing) {
      // An already-claimed lead is an ERROR, not a quiet success: the claim is
      // immutable and the person's real profile supersedes anything a lead could
      // pre-fill, so returning its id would toast "Lead created" while nothing
      // happened and nothing could.
      if (existing.claimedBy) {
        throw leadRefusal(
          "23505",
          `this person has already claimed their profile (lead ${existing.id}); there is nothing left to pre-fill`,
        );
      }
      return existing.id;
    }

    const now = new Date().toISOString();
    const lead: AdminLead = {
      // NOT minted from MOCK_LEADS.length — that is the bug
      // MockStaffingService.createLeadManual.test.ts was written to pin: a create
      // after a delete reuses a number and collides with a live row. Derived from
      // the candidate instead, which is unique and, since this method is
      // idempotent per person, stable across repeat clicks.
      id: `mock-lead-from-${candidateId}`,
      email,
      phone,
      displayName: name,
      locationCity: c.locationCity,
      locationCountry: c.locationCountry,
      skills: c.skills ?? [],
      employmentTitle: c.employmentTitle,
      source: "candidate",
      sourceCandidateId: c.id,
      candidateIdentityId: c.candidateIdentityId,
      createdBy: "did:privy:mock-admin",
      notes: null,
      claimedBy: null,
      claimedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    MOCK_LEADS = [lead, ...MOCK_LEADS];
    return lead.id;
  }

  async createLeadManual(input: LeadCreateInput): Promise<string> {
    await delay(MOCK_DELAY_MS);
    // The RPC's refusals, reproduced — not because mock mode needs to be a
    // database, but because DEV-819's whole point is that a duplicate contact
    // and a suppressed one arrive as DISTINCT readable errors. A mock that
    // always succeeded would let that regress unnoticed in mock mode, which is
    // where the form is demoed. All SIX refusals are here, in the RPC's own
    // order: blank name, no contact, a non-E.164 phone, an email that already
    // belongs to a registered user via MOCK_EXISTING_USER_EMAILS, a contact
    // already held (claimed or not), and an opted-out contact via
    // MOCK_SUPPRESSED_CONTACTS. Keep this count honest — the enumeration said
    // five while the RPC refused six, and that is precisely how the
    // existing-user refusal came to be missing from this method.
    if (!input.displayName.trim()) {
      throw leadRefusal("23514", "a lead needs a name; a placeholder would overwrite the claimer's own login name");
    }
    if (!input.email && !input.phone) {
      throw leadRefusal("23514", "a lead needs an email or a phone; without one it can never be claimed");
    }
    if (input.phone && !isLeadE164(input.phone)) {
      throw leadRefusal(
        "23514",
        `phone must be in international format, e.g. +595981123456 (got ${input.phone}); the country code cannot be guessed`,
      );
    }

    // NORMALIZED BEFORE COMPARING, because that is what makes the duplicate
    // checks below mean "one lead per person". `leads_normalize_biu` lowercases
    // the email and canonicalizes the phone before the partial unique indexes
    // ever see them, so a mock that compared the RAW typed value would accept
    // "+595 981-123456" against a stored "+595981123456" — a formatted duplicate
    // that production refuses. That is the exact regression this mock exists to
    // prevent, so the normalized values are also what gets STORED below.
    const email = input.email?.trim().toLowerCase() || null;
    const phone = input.phone ? normalizeLeadPhone(input.phone) : null;

    // ALREADY A USER: REFUSE. Probed here — after normalization, before the
    // duplicate checks — because that is where admin_create_lead_manual probes
    // it, and the order is observable: a contact that is both already held and
    // already a user reports whichever check runs first.
    if (email && MOCK_EXISTING_USER_EMAILS.includes(email)) {
      throw leadRefusal(
        "23514",
        `${email} already belongs to a registered user; a lead only pre-fills a profile at FIRST login, so this one could never be claimed — edit their profile instead`,
      );
    }

    // Email first, then phone — never one OR-ed check. The real RPC probes each
    // contact against its own partial unique index precisely so the message
    // names the row that genuinely holds what was typed; an OR-ed check here
    // could name a different one and hide that difference.
    const byEmail = email ? MOCK_LEADS.find((l) => l.email === email) : undefined;
    if (byEmail) {
      // A CLAIMED conflict reports differently, mirroring the RPC: "open that
      // lead instead" is a dead end for a row whose detail panel is read-only.
      if (byEmail.claimedBy) {
        throw leadRefusal(
          "23505",
          `this person has already claimed their profile (lead ${byEmail.id}); there is nothing left to pre-fill`,
        );
      }
      throw leadRefusal(
        "23505",
        `a lead (${byEmail.id}) already holds the email ${email}; open that lead instead of creating a second one`,
      );
    }
    const byPhone = phone ? MOCK_LEADS.find((l) => l.phone === phone) : undefined;
    if (byPhone) {
      if (byPhone.claimedBy) {
        throw leadRefusal(
          "23505",
          `this person has already claimed their profile (lead ${byPhone.id}); there is nothing left to pre-fill`,
        );
      }
      throw leadRefusal(
        "23505",
        `a lead (${byPhone.id}) already holds the phone ${phone}; open that lead instead of creating a second one`,
      );
    }

    // The refusal the trigger owns in production. Checked AFTER the duplicate
    // probes, matching the real ordering: a contact that is both already held and
    // suppressed reports the duplicate, because that is the row the admin can act
    // on.
    if (
      (email && MOCK_SUPPRESSED_CONTACTS.emails.includes(email)) ||
      (phone && MOCK_SUPPRESSED_CONTACTS.phones.includes(phone))
    ) {
      throw leadRefusal("23514", "contact is suppressed (opted out); refusing to write a lead for it");
    }

    const now = new Date().toISOString();
    const lead: AdminLead = {
      // NOT `MOCK_LEADS.length + 1`: deleteLead shrinks the array, so a create
      // after a delete would mint an id a surviving row already holds, and the
      // list's `find(l => l.id === selectedId)` would open the wrong lead.
      id: `mock-lead-manual-${++mockManualLeadSeq}`,
      email,
      phone,
      displayName: input.displayName.trim(),
      locationCity: input.locationCity,
      locationCountry: input.locationCountry,
      skills: input.skills,
      employmentTitle: input.employmentTitle,
      // Provenance the RPC sets, not the caller: a hand-entered lead has no
      // originating candidate, which is why the detail panel's provenance card
      // treats a null as an ordinary answer rather than a missing link.
      source: "manual",
      sourceCandidateId: null,
      candidateIdentityId: null,
      createdBy: "did:privy:mock-admin",
      notes: input.notes,
      claimedBy: null,
      claimedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    // Prepended, and the row is really added: the list sorts by createdAt
    // descending, so a lead an admin just created must appear at the top rather
    // than vanish until a reload.
    MOCK_LEADS = [lead, ...MOCK_LEADS];
    return lead.id;
  }

  /**
   * DEV-820: the mock's CSV importer.
   *
   * WHY THE CLASSIFICATION IS REPRODUCED HERE AT ALL. Mock mode is where the
   * import dialog is demoed, and for this feature the REPORT is the feature — a
   * mock that returned ten `inserted` rows would make the grouped report look
   * right while proving nothing about it. So all ten outcomes are produced, in
   * `admin_import_leads`'s own order, using the same fixtures
   * `createLeadManual` uses for its refusals (MOCK_SUPPRESSED_CONTACTS,
   * MOCK_EXISTING_USER_EMAILS).
   *
   * A REFUSED ROW RESOLVES, IT NEVER THROWS. Only a failure of the CALL throws,
   * and there is exactly one: over the row cap. That asymmetry is the port
   * contract — see `importLeads` in IStaffingAdminService — and reproducing it
   * is the point, because a mock that threw on the first suppressed contact
   * would make the dialog look broken in the demo and correct in production.
   *
   * `v_seen` HAS AN EQUIVALENT HERE, and it matters for the same reason. The
   * real RPC tracks in-file contacts in a plpgsql variable rather than by
   * querying the table, because in a dry run each insert is rolled back before
   * the next row is classified. A mock that detected `duplicate_in_file` by
   * consulting MOCK_LEADS would therefore agree on a commit and DISAGREE on a
   * preview — the one asymmetry this method has to get right.
   */
  async importLeads(
    rows: LeadImportRow[],
    opts: { dryRun: boolean },
  ): Promise<LeadImportReport> {
    await delay(MOCK_DELAY_MS);

    // The only failure of the CALL. Same SQLSTATE the RPC raises, so a caller
    // branching on `cause.code` behaves identically in mock mode.
    if (rows.length > LEAD_IMPORT_MAX_ROWS) {
      throw leadRefusal(
        "23514",
        `this import has ${rows.length} rows; the importer takes up to ${LEAD_IMPORT_MAX_ROWS} at a time — split the file and import it in parts`,
      );
    }

    // The in-file contacts accepted so far. A local, NOT a query against
    // MOCK_LEADS — see the note above about why the real RPC does the same.
    const seen = new Set<string>();
    const created: AdminLead[] = [];
    const results: LeadImportRowResult[] = [];

    const report = (
      line: number,
      outcome: LeadImportOutcome,
      leadId: string | null,
      message: string | null,
    ) => results.push({ line, outcome, leadId, message });

    for (const { line, input } of rows) {
      // 1. A nameless lead is refused: leads.display_name is NOT NULL, and a
      // truthy placeholder would beat the claimer's own login name.
      if (!input.displayName.trim()) {
        report(line, "no_name", null,
          "this row has no name; a placeholder would overwrite the person's own login name");
        continue;
      }

      // NORMALIZED BEFORE COMPARING, for the reason createLeadManual's own
      // comment gives: `leads_normalize_biu` canonicalizes both contacts before
      // the partial unique indexes see them, so a mock comparing the RAW value
      // would accept a formatted duplicate that production refuses.
      const email = input.email?.trim().toLowerCase() || null;

      // 2. Email shape — the check `admin_import_leads` adds and
      // `admin_create_lead_manual` leaves to the client.
      //
      // `isLeadEmailPlausible`, NOT `CANDIDATE_EMAIL_PATTERN`, which this used
      // to use and which is materially looser: it accepts `a@b.c` and
      // `trailing@x.com.`, both of which the RPC refuses, so the mock reported
      // `inserted` where production reports `bad_email`. See the constant's own
      // comment — it is the character-for-character mirror of the SQL.
      if (email && !isLeadEmailPlausible(email)) {
        report(line, "bad_email", null,
          `${email} does not look like an email address; a lead with an unmatchable contact could never be claimed`);
        continue;
      }

      // 3. Phone policy is REFUSE, not drop — the admin's file said this.
      let phone: string | null = null;
      if (input.phone) {
        if (!isLeadE164(input.phone)) {
          report(line, "bad_phone", null,
            `${input.phone} is not in international format, e.g. +595981123456; the country code cannot be guessed`);
          continue;
        }
        phone = normalizeLeadPhone(input.phone);
      }

      // 4. CONSTRAINT leads_has_contact: without a contact the lead can never
      // be claimed.
      if (!email && !phone) {
        report(line, "no_contact", null,
          "this row has neither an email nor a phone; without one the lead could never be claimed");
        continue;
      }

      // 5. An earlier row of this same file already took the contact.
      if ((email && seen.has(email)) || (phone && seen.has(phone))) {
        report(line, "duplicate_in_file", null,
          "an earlier row of this file already holds this contact");
        continue;
      }

      // 6. Already a user. EMAIL ONLY, mirroring the RPC's documented limit:
      // `profiles` has no phone column, so a phone-only row for an existing
      // user is still imported.
      if (email && MOCK_EXISTING_USER_EMAILS.includes(email)) {
        report(line, "existing_user", null,
          `${email} already belongs to a registered user; a lead only pre-fills a profile at FIRST login, so this one could never be claimed`);
        continue;
      }

      // 7. A lead already holds this contact. Email first, then phone — never
      // one OR-ed check, so the report names the row that genuinely holds what
      // the file said.
      //
      // ONLY THE STORED LEADS ARE SEARCHED, not the rows this call just built:
      // `seen` is populated on exactly the same condition as `created`, so a
      // contact from an earlier row of this file has already been reported as
      // `duplicate_in_file` above and never reaches here. Adding them would be
      // an unreachable branch implying the two checks can disagree.
      const held =
        (email ? MOCK_LEADS.find((l) => l.email === email) : undefined) ??
        (phone ? MOCK_LEADS.find((l) => l.phone === phone) : undefined);
      if (held) {
        report(line, "duplicate_lead", held.id,
          `a lead (${held.id}) already holds this contact; nothing was changed`);
        continue;
      }

      // 8. The suppression refusal the trigger owns in production. Checked
      // AFTER the duplicate probe, matching createLeadManual's ordering: a
      // contact that is both already held and suppressed reports the duplicate,
      // because that is the row an admin can act on.
      if (
        (email && MOCK_SUPPRESSED_CONTACTS.emails.includes(email)) ||
        (phone && MOCK_SUPPRESSED_CONTACTS.phones.includes(phone))
      ) {
        report(line, "suppressed", null,
          "this contact has opted out; they must not become a pre-filled profile");
        continue;
      }

      const now = new Date().toISOString();
      const lead: AdminLead = {
        // Monotonic, so an import after a delete cannot reuse a live row's id.
        id: `mock-lead-csv-${++mockManualLeadSeq}`,
        email,
        phone,
        displayName: input.displayName.trim(),
        locationCity: input.locationCity,
        locationCountry: input.locationCountry,
        skills: input.skills,
        employmentTitle: input.employmentTitle,
        // Provenance the importer sets, never the payload.
        source: "csv",
        sourceCandidateId: null,
        candidateIdentityId: null,
        createdBy: "did:privy:mock-admin",
        notes: input.notes,
        claimedBy: null,
        claimedAt: null,
        createdAt: now,
        updatedAt: now,
      };

      // SEEN IS RECORDED IN BOTH MODES, the row only in a commit. That is the
      // mock's version of the real function remembering a contact in a plpgsql
      // variable that survives the dry run's subtransaction rollback.
      if (email) seen.add(email);
      if (phone) seen.add(phone);
      created.push(lead);
      // NO LEAD ID FOR A DRY-RUN INSERT, because no lead exists. The RPC
      // returns NULL here for the same reason: its insert has been rolled back,
      // so an id would name a row that is already gone. Handing one back would
      // let a caller link a preview row to a lead detail page that 404s, and
      // would be Mock/Supabase drift in the direction that hides the bug.
      report(line, "inserted", opts.dryRun ? null : lead.id, null);
    }

    if (!opts.dryRun && created.length > 0) {
      // Prepended, and really added: the list sorts by createdAt descending, so
      // rows an admin just imported must appear at the top rather than vanish
      // until a reload. Reversed so the file's own order survives that.
      MOCK_LEADS = [...[...created].reverse(), ...MOCK_LEADS];
    }

    return { dryRun: opts.dryRun, rows: results, counts: countLeadImport(results) };
  }

  // ─── DEV-817: the Leads admin view ─────────────────────────────────────────
  // Filters are applied here rather than ignored, so mock mode exercises the
  // same empty/filtered states the real adapter produces.

  async listLeads(filters: AdminLeadFilters): Promise<PaginatedResult<AdminLead>> {
    await delay(MOCK_DELAY_MS);
    const limit = filters.limit ?? 50;
    const offset = filters.offset ?? 0;

    let rows = [...MOCK_LEADS].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    const search = filters.search?.trim().toLowerCase();
    if (search) {
      // Same three columns the real query searches, in the same order.
      rows = rows.filter(
        (l) =>
          (l.email ?? "").toLowerCase().includes(search) ||
          (l.phone ?? "").toLowerCase().includes(search) ||
          l.displayName.toLowerCase().includes(search),
      );
    }
    if (filters.source) rows = rows.filter((l) => l.source === filters.source);
    if (filters.claim === "claimed") rows = rows.filter((l) => l.claimedBy !== null);
    else if (filters.claim === "unclaimed") rows = rows.filter((l) => l.claimedBy === null);

    const total = rows.length;
    const page = rows.slice(offset, offset + limit);
    const nextOffset = offset + limit;

    return {
      data: page,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async getLeadSourceCandidate(leadId: string): Promise<LeadSourceCandidate | null> {
    await delay(MOCK_DELAY_MS);
    const lead = MOCK_LEADS.find((l) => l.id === leadId);
    // null, not a throw: a manual/csv lead genuinely has nothing to link to, and
    // the port documents that as an ordinary answer.
    if (!lead?.sourceCandidateId) return null;
    return {
      candidateId: lead.sourceCandidateId,
      questId: "mock-quest-1",
      questTitle: "Rewire the workshop",
      candidateName: lead.displayName,
      candidateStatus: "reviewed",
    };
  }

  async updateLead(id: string, input: LeadUpdateInput): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const lead = MOCK_LEADS.find((l) => l.id === id);
    if (!lead) throw new Error(`Lead not found: ${id}`);
    // The refusals the RPC raises, reproduced so mock mode shows the same
    // error toasts rather than a success the real backend would refuse.
    if (lead.claimedBy) {
      throw new Error(
        `lead ${id} has been claimed; its facts belong to a real profile now — edit that profile instead`,
      );
    }
    if (!input.displayName.trim()) {
      throw new Error("a lead needs a name; a placeholder would overwrite the claimer's own login name");
    }
    if (!input.email && !input.phone) {
      throw new Error("a lead needs an email or a phone; without one it can never be claimed");
    }
    const clash = MOCK_LEADS.find(
      (l) =>
        l.id !== id &&
        ((input.email && l.email === input.email) || (input.phone && l.phone === input.phone)),
    );
    if (clash) {
      throw new Error(`another lead (${clash.id}) already holds that contact; merge them instead`);
    }
    Object.assign(lead, input, { updatedAt: new Date().toISOString() });
  }

  async deleteLead(id: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const lead = MOCK_LEADS.find((l) => l.id === id);
    if (!lead) throw new Error(`lead ${id} no longer exists; it may already have been deleted`);
    if (lead.claimedBy) {
      throw new Error(
        `lead ${id} has been claimed; a claimed lead is retained as the record of what was pre-filled`,
      );
    }
    MOCK_LEADS = MOCK_LEADS.filter((l) => l.id !== id);
  }

  async reenrichCandidate(candidateId: string): Promise<ReenrichCandidateResult> {
    await delay(MOCK_DELAY_MS * 3);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === candidateId);
    if (!row) throw new Error(`Candidate not found: ${candidateId}`);
    // Deterministic fixture: no real paid lookup in mock mode, so always report
    // a miss — exercises the "no_email_found" UI path without a fake contact.
    return { status: "no_email_found" };
  }

  /**
   * Deterministic fixture ids so mock-admin mode can drive every modal state
   * without a worker. Anything else reports a clean send.
   */
  async sendTestbedOutreach(input: {
    testbedCandidateId: string;
    channel: "email" | "manual" | "sms";
    subject: string;
    body: string;
  }): Promise<TestbedOutreachResult> {
    await delay(MOCK_DELAY_MS);
    if (input.channel === "manual") return { status: "copied" };
    if (input.testbedCandidateId === "tc-cooldown") return { status: "skipped", reason: "cooldown" };
    if (input.testbedCandidateId === "tc-suppressed") return { status: "skipped", reason: "suppressed" };
    if (input.testbedCandidateId === "tc-disabled") return { status: "skipped", reason: "disabled" };
    if (input.testbedCandidateId === "tc-failed") return { status: "failed", reason: "invalid recipient" };
    return { status: "sent" };
  }

  /**
   * Deterministic fixture ids so mock-admin mode can drive every Outbox send
   * state without a worker. Anything else reports a clean send.
   */
  async sendOutreachDraft(draftId: string): Promise<SendOutreachDraftResult> {
    await delay(MOCK_DELAY_MS);
    if (draftId === "d-cooldown") return { status: "skipped", reason: "cooldown" };
    if (draftId === "d-suppressed") return { status: "skipped", reason: "suppressed" };
    if (draftId === "d-disabled") return { status: "skipped", reason: "disabled" };
    // The two misconfigured families, which need OPPOSITE repairs and so must
    // both be drivable here: named env gaps (set a var) versus a stale stored
    // body (rebuild the drafts — no var fixes it).
    if (draftId === "d-misconfigured") {
      return {
        status: "skipped",
        reason: "misconfigured",
        missing: ["OUTREACH_FROM_EMAIL", "OUTREACH_HMAC_SECRET"],
      };
    }
    if (draftId === "d-stale-links") {
      return { status: "skipped", reason: "misconfigured", missing: ["draft_links"] };
    }
    if (draftId === "d-failed") return { status: "failed", reason: "invalid recipient" };
    if (draftId === "d-stale") return { status: "not_approved", current: "sent" };
    return { status: "sent" };
  }

  async runHeadhunterForQuest(
    questId: string,
    _opts?: { forceReunderstand?: boolean; skipInternal?: boolean; skipExternal?: boolean; platforms?: string[]; targetType?: SearchTargetType },
  ): Promise<RunHeadhunterResult> {
    await delay(MOCK_DELAY_MS);
    if (questId === "missing-quest") throw new Error("quest not found");
    // Deterministic fixture: hook/component tests assert this count verbatim.
    return { ok: true, count: 3 };
  }

  async searchCandidatesTestbed(_params: SearchTestbedParams): Promise<SearchTestbedResult> {
    await delay(MOCK_DELAY_MS);
    // Deterministic fixtures for hook/component tests to assert against verbatim.
    return {
      candidates: [
        {
          source: "external",
          memberId: "mock-1",
          fullName: "Ada Lovelace",
          locationCity: "London",
          locationCountry: "UK",
          skills: ["TypeScript", "React"],
          employmentTitle: "Senior Software Engineer",
          linkedinUrl: "https://linkedin.com/in/mock-1",
          email: null,
          phone: null,
          emailVerified: false,
          score: 0,
        },
        {
          source: "external",
          memberId: "mock-2",
          fullName: "Grace Hopper",
          locationCity: "New York",
          locationCountry: "USA",
          skills: ["Node.js"],
          employmentTitle: "Staff Engineer",
          linkedinUrl: "https://linkedin.com/in/mock-2",
          email: null,
          phone: null,
          emailVerified: false,
          score: 0,
        },
      ],
      // DEV-494: fixture spend so mock-admin mode renders the wallet/cost strip
      // with real-looking numbers instead of three "—" cards.
      spend: {
        runCostUsd: 0.042,
        spentTodayUsd: 1.37,
        dailyCapUsd: 5,
        walletBalanceUsd: 18.64,
      },
    };
  }

  async parseSearchQuery(_query: string): Promise<ParsedSearchQuery> {
    await delay(MOCK_DELAY_MS);
    // Deterministic fixture for hook/component tests to assert against verbatim.
    return {
      titles: ["Senior Software Engineer"],
      skills: ["typescript", "react"],
      location: "London, UK",
    };
  }

  // DEV-450: fixture other-quest data so mock-admin mode can exercise the
  // badge/expansion without hitting Supabase.
  async getOtherQuestsForIdentity(candidateIdentityId: string, excludeQuestId?: string): Promise<OtherQuestMatch[]> {
    await delay(MOCK_DELAY_MS);
    const matches = MOCK_OTHER_QUESTS[candidateIdentityId] ?? [];
    return excludeQuestId ? matches.filter((q) => q.questId !== excludeQuestId) : matches;
  }

  // ─── Headhunter / Staffing (DEV-351) ──────────────────────
  async listHeadhunterCandidates(
    filters: AdminCandidateFilters,
  ): Promise<PaginatedResult<AdminQuestCandidate>> {
    await delay(MOCK_DELAY_MS);
    let rows = MOCK_HEADHUNTER_CANDIDATES;
    if (filters.chainId != null) {
      rows = rows.filter((c) => mockChainForQuest(c.questId) === filters.chainId);
    }
    // The narrowest scope, applied first and mirroring the RPC's `p_quest_id`.
    //
    // Its ABSENCE was a bug, not an omission: the Supabase adapter has always
    // forwarded `filters.questId`, so mock-admin — which is how this flow is
    // actually developed and reviewed — returned every quest's candidates for a
    // query that production would have scoped to one. A panel wired correctly
    // still looked broken locally, and a panel wired incorrectly still looked
    // fine. See the note on `ProspectsPanel`'s `questId` prop.
    if (filters.questId) {
      rows = rows.filter((c) => c.questId === filters.questId);
    }
    if (filters.source && filters.source !== "all") {
      rows = rows.filter((c) => c.source === filters.source);
    }
    if (filters.status && filters.status !== "all") {
      rows = rows.filter((c) => c.status === filters.status);
    }
    if (filters.hasContact) {
      rows = rows.filter((c) => c.email || c.phone);
    }
    if (filters.unconverted) {
      rows = rows.filter((c) => c.convertedUserId == null);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      rows = rows.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          (c.questTitle ?? "").toLowerCase().includes(q),
      );
    }
    // W-C match-tier / facet filters — mirror the admin_list_quest_candidates RPC
    // semantics so mock-admin behaves like production.
    // Trim to match the Supabase adapter, which passes `filters.location?.trim()`
    // to the RPC — so a padded facet behaves identically in mock-admin.
    const location = filters.location?.trim();
    if (location) {
      const q = location.toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.locationCity ?? "").toLowerCase().includes(q) ||
          (c.locationCountry ?? "").toLowerCase().includes(q),
      );
    }
    const skill = filters.skill?.trim();
    if (skill) {
      const q = skill.toLowerCase();
      rows = rows.filter((c) => c.skills.some((s) => s.toLowerCase().includes(q)));
    }
    // Tier via score range — apply each clause independently, mirroring the RPC's
    // ANDed WHERE (unscored = null/negative; min/max = half-open band). tierScoreFilter
    // guarantees `unscored` and the numeric band are never sent together; if a direct
    // caller sends both, the clauses intersect to the empty set exactly as the SQL does.
    if (filters.unscored) {
      rows = rows.filter((c) => c.score == null || c.score < 0);
    }
    if (filters.minScore != null) {
      rows = rows.filter((c) => c.score != null && c.score >= filters.minScore!);
    }
    if (filters.maxScore != null) {
      rows = rows.filter((c) => c.score != null && c.score < filters.maxScore!);
    }
    // Sort — default score DESC NULLS LAST, matching the RPC's ORDER BY. Nulls sort
    // last for every key/direction; a created-desc tiebreaker keeps it stable.
    rows = sortCandidates(rows, filters.sort ?? "score", filters.sortDir ?? "desc");
    // Honor limit/offset like the Supabase adapter so AdminPagination behaves in
    // dev: total is the full filtered count, data is the requested page.
    const total = rows.length;
    const offset = filters.offset ?? 0;
    const limit = filters.limit ?? total;
    const page = rows.slice(offset, offset + limit);
    // DEV-822: resolve lead state LAST, over the page only — mirroring the RPC,
    // where the LATERAL is part of the same read rather than anything stored on
    // the candidate. Resolving here (not in the seed rows) is what makes
    // createLeadFromCandidate visibly change the row on the next refetch, which
    // is the ticket's acceptance criterion and is checked by hand in mock mode.
    //
    // ASSIGNED ONTO THE FIXTURE, NOT SPREAD INTO A COPY, and that is not a style
    // choice. This method returns the fixture objects THEMSELVES, and
    // MockStaffingService.clearCandidates.test.ts documents and depends on it —
    // mutating a returned row is how that test marks a candidate converted.
    // Returning `page.map((c) => ({ ...c }))` silently breaks it: the mutation
    // lands on a copy the store never sees, and the test fails two steps later
    // with an unrelated-looking count.
    //
    // Recomputed on every read, so nothing here can go stale — and
    // resolveCandidateLeadState reads only id/email/phone, never the fields it
    // writes, so assigning back cannot feed the next resolution.
    for (const c of page) Object.assign(c, resolveCandidateLeadState(c));
    return { data: page, total, cursor: null, hasMore: offset + page.length < total };
  }

  async inviteMemberToVolunteer(
    candidateId: string,
  ): Promise<{ proposalId: string; taskId: string; created: boolean }> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === candidateId);
    if (!row) throw new Error(`Candidate not found: ${candidateId}`);
    // Mirrors the RPC's first refusal. The quest-side checks (budget, open
    // spot) have no mock quest to consult.
    if (row.source !== "internal" || !row.memberId) {
      throw new Error("only an internal member can be invited to volunteer");
    }
    const created = row.status !== "invited";
    row.status = "invited";
    row.outreachSentAt ??= new Date().toISOString();
    return { proposalId: `mock-proposal-${candidateId}`, taskId: `mock-task-${row.questId}`, created };
  }

  // 20260923120000: three mock members asked for volunteer quests; each quest
  // alerts them once, like the RPC's dedupe log.
  private volunteerAlerted = new Set<string>();

  async countVolunteerAlertRecipients(questId: string): Promise<number> {
    await delay(MOCK_DELAY_MS);
    return this.volunteerAlerted.has(questId) ? 0 : 3;
  }

  async sendVolunteerQuestAlert(questId: string): Promise<{ sent: number; skipped: number }> {
    await delay(MOCK_DELAY_MS);
    if (this.volunteerAlerted.has(questId)) return { sent: 0, skipped: 0 };
    this.volunteerAlerted.add(questId);
    return { sent: 3, skipped: 0 };
  }

  async setCandidateStatus(
    id: string,
    status: AdminSettableCandidateStatus,
    dismissalReason?: import("@/lib/types").DismissalReason | null,
    dismissalNote?: string | null,
  ): Promise<{
    status: AdminSettableCandidateStatus;
    outreachSentAt: string | null;
    dismissalReason: import("@/lib/types").DismissalReason | null;
    dismissalNote: string | null;
  }> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === id);
    if (!row) throw new Error(`Candidate not found: ${id}`);
    row.status = status;
    // 'invited' = reached out: stamp first contact, mirroring the RPC.
    if (status === "invited" && row.outreachSentAt == null) {
      row.outreachSentAt = new Date().toISOString();
    }
    // Mirror RPC: write dismissal fields on dismiss, clear on any other status.
    if (status === "dismissed") {
      row.dismissalReason = (dismissalReason ?? null) as typeof row.dismissalReason;
      row.dismissalNote = dismissalNote ?? null;
    } else {
      row.dismissalReason = null;
      row.dismissalNote = null;
    }
    return {
      status,
      outreachSentAt: row.outreachSentAt,
      dismissalReason: row.dismissalReason,
      dismissalNote: row.dismissalNote,
    };
  }

  async addQuestCandidate(input: QuestCandidateCreateInput): Promise<string> {
    await delay(MOCK_DELAY_MS);
    // The RPC's refusals, reproduced — not because mock mode needs to be a
    // database, but because the whole point is that a duplicate contact for the
    // SAME QUEST and a blank name arrive as DISTINCT readable errors. A mock
    // that always succeeded would let that regress unnoticed in mock mode.
    if (!input.fullName.trim()) {
      throw leadRefusal("23514", "a candidate needs a name; a placeholder would be meaningless in the admin UI");
    }
    const email = input.email?.trim().toLowerCase() || null;
    const phone = input.phone ? normalizeLeadPhone(input.phone) : null;
    const linkedin = input.linkedinUrl?.trim().toLowerCase() || null;
    if (!email && !phone && !linkedin) {
      throw leadRefusal("23514", "a candidate needs an email, a phone, or a LinkedIn URL; without one they can never be reached");
    }
    if (input.phone && !isLeadE164(input.phone)) {
      throw leadRefusal(
        "23514",
        `phone must be in international format, e.g. +595981123456 (got ${input.phone}); the country code cannot be guessed`,
      );
    }

    // Check if the quest exists in mock data.
    const questExists = MOCK_HEADHUNTER_CANDIDATES.some((c) => c.questId === input.questId)
      || MOCK_OUTREACH_DRAFTS.some((d) => d.questId === input.questId);
    if (!questExists && !input.questId.startsWith("mock-quest") && !input.questId.startsWith("admin-quest")) {
      throw leadRefusal("23514", `quest ${input.questId} does not exist; pick a valid quest before adding a candidate`);
    }

    // Quest-scoped dedupe: check if this quest already holds a candidate with
    // the same identity (matching email, phone, or linkedin).
    const existing = MOCK_HEADHUNTER_CANDIDATES.find((c) => {
      if (c.questId !== input.questId) return false;
      if (linkedin && c.linkedinUrl?.toLowerCase() === linkedin) return true;
      if (email && c.email?.toLowerCase() === email) return true;
      if (phone && c.phone === phone) return true;
      return false;
    });
    if (existing) {
      throw leadRefusal(
        "23505",
        `this quest already holds a candidate for this person (candidate ${existing.id}, name "${existing.fullName}"); add them to a different quest instead`,
      );
    }

    const now = new Date().toISOString();
    const candidate: AdminQuestCandidate = {
      id: `mock-cand-manual-${++mockManualCandidateSeq}`,
      questId: input.questId,
      questTitle: null,
      source: "external",
      memberId: null,
      convertedUserId: null,
      fullName: input.fullName.trim(),
      locationCity: input.locationCity ?? null,
      locationCountry: input.locationCountry ?? null,
      skills: input.skills ?? [],
      employmentTitle: input.employmentTitle ?? null,
      linkedinUrl: linkedin ?? null,
      email: email ?? null,
      phone: phone ?? null,
      emailVerified: null,
      score: null,
      status: "suggested",
      outreachSentAt: null,
      sourceUrl: null,
      collectedAt: now,
      lawfulBasis: "manual_admin_entry",
      purpose: null,
      lastRefreshedAt: null,
      createdAt: now,
      linkedinOutreachStatus: "none",
      linkedinSentAt: null,
      dismissalReason: null,
      dismissalNote: null,
      sourceUrls: [],
      aiProfile: null,
      aiProfileGeneratedAt: null,
      aiAssessment: null,
      aiAssessmentGeneratedAt: null,
      resumeUrl: null,
      aiEnrichedFields: [],
      candidateIdentityId: null,
      otherQuestCount: 0,
      sourcePlatform: null,
      socialProfiles: null,
      entityType: "person",
      mapsPlaceId: null,
      formattedAddress: null,
      mapsUri: null,
      skillsFromQuery: false,
      locationFromQuery: false,
      memberEmail: null,
      leadId: null,
      leadClaimedAt: null,
      manuallyAdded: true,
      referral: null,
      volunteerHistory: null,
    };
    MOCK_HEADHUNTER_CANDIDATES.unshift(candidate);
    return candidate.id;
  }

  async setSourceUrls(candidateId: string, urls: string[]): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === candidateId);
    if (!row) throw new Error(`Candidate not found: ${candidateId}`);
    row.sourceUrls = urls;
  }

  async generateCandidateProfile(
    candidateId: string,
  ): Promise<{ profile: string; generatedAt: string }> {
    await delay(MOCK_DELAY_MS * 3);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === candidateId);
    if (!row) throw new Error(`Candidate not found: ${candidateId}`);
    const profile = `${row.fullName} is a skilled professional with experience in ${(row.skills ?? []).slice(0, 3).join(", ") || "various areas"}.`;
    row.aiProfile = profile;
    row.aiProfileGeneratedAt = new Date().toISOString();
    return { profile, generatedAt: row.aiProfileGeneratedAt };
  }

  async generateCandidateAssessment(
    candidateId: string,
  ): Promise<{ assessment: CandidateAiAssessment; generatedAt: string }> {
    await delay(MOCK_DELAY_MS * 3);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === candidateId);
    if (!row) throw new Error(`Candidate not found: ${candidateId}`);
    const topSkills = (row.skills ?? []).slice(0, 2);
    const assessment: CandidateAiAssessment = {
      verdict: `${row.fullName} looks like a strong fit based on their background.`,
      pros: topSkills.length > 0 ? topSkills.map((s) => `Matches required skill: ${s}`) : ["Relevant professional background"],
      cons: [],
    };
    row.aiAssessment = assessment;
    row.aiAssessmentGeneratedAt = new Date().toISOString();
    return { assessment, generatedAt: row.aiAssessmentGeneratedAt };
  }

  async generateOutreachMessage(
    draftId: string,
  ): Promise<{ paragraph: string; generatedAt: string }> {
    await delay(MOCK_DELAY_MS * 3);
    const draft = MOCK_OUTREACH_DRAFTS.find((d) => d.id === draftId);
    if (!draft) throw new Error(`Draft not found: ${draftId}`);
    const topSkills = (draft.candidateSkills ?? []).slice(0, 2);
    const paragraph = draft.candidateAiProfile
      ? `${draft.candidateAiProfile} This background lines up well with ${draft.questTitle ?? "this quest"}.`
      : `${draft.fullName ?? "This candidate"} brings experience in ${topSkills.join(", ") || "relevant areas"}, which fits well with ${draft.questTitle ?? "this quest"}.`;
    return { paragraph, generatedAt: new Date().toISOString() };
  }

  async setCandidateResumeUrl(id: string, url: string | null): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === id);
    if (!row) throw new Error(`Candidate not found: ${id}`);
    row.resumeUrl = url;
  }

  async setCandidateEmail(id: string, email: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === id);
    if (!row) throw new Error(`Candidate not found: ${id}`);
    const trimmed = email.trim();
    if (!CANDIDATE_EMAIL_PATTERN.test(trimmed)) {
      throw new Error(`Invalid email address: ${trimmed}`);
    }
    row.email = trimmed;
    row.emailVerified = false;
  }

  async updateCandidate(id: string, input: CandidateUpdateInput): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === id);
    if (!row) throw new Error(`Candidate not found: ${id}`);
    if (input.fullName !== undefined) row.fullName = input.fullName ?? "";
    if (input.email !== undefined) {
      const email = input.email ? input.email.trim() : null;
      if (email && !CANDIDATE_EMAIL_PATTERN.test(email)) {
        throw new Error(`Invalid email address: ${email}`);
      }
      row.email = email;
      row.emailVerified = false;
    }
    const PHONE_PATTERN = /^\+?[\d\s\-().]+$/;
    if (input.phone !== undefined) {
      const phone = input.phone ? input.phone.trim() : null;
      if (phone && !PHONE_PATTERN.test(phone)) {
        throw new Error(`Invalid phone number: ${phone}`);
      }
      row.phone = phone;
    }
    if (input.linkedinUrl !== undefined) row.linkedinUrl = input.linkedinUrl ?? null;
    if (input.employmentTitle !== undefined) row.employmentTitle = input.employmentTitle ?? null;
    if (input.locationCity !== undefined) row.locationCity = input.locationCity ?? null;
    if (input.skills !== undefined) row.skills = input.skills;
  }

  /**
   * The mock has no rendered drafts to rebuild — the Outbox fixtures are
   * hand-written HTML, not template output — so this reports the honest zero
   * rather than a plausible-looking count the UI would then toast about.
   */
  async refreshCandidateDrafts(_candidateId: string): Promise<CandidateDraftRefresh> {
    return { rerendered: 0, staleBodies: 0 };
  }

  async clearQuestCandidates(
    questId: string,
    source: "internal" | "external",
  ): Promise<number> {
    await delay(MOCK_DELAY_MS);
    // Mirrors admin_clear_quest_candidates, including its source validation —
    // a bad source raises rather than matching zero rows and reporting success.
    if (source !== "internal" && source !== "external") {
      throw new Error(`Invalid source: ${source}`);
    }
    const doomed = MOCK_HEADHUNTER_CANDIDATES.filter(
      (c) =>
        c.questId === questId &&
        c.source === source &&
        c.convertedUserId == null &&
        // Internal 'invited' rows are the only record that weXare contacted a
        // member — quest-notify writes no outreach_log equivalent, so unlike an
        // invited external they cannot be reconstructed after deletion.
        !(source === "internal" && c.status === "invited"),
    );
    for (const row of doomed) {
      MOCK_HEADHUNTER_CANDIDATES.splice(MOCK_HEADHUNTER_CANDIDATES.indexOf(row), 1);
    }
    // outreach_drafts have an ON DELETE CASCADE FK to quest_candidates, so the
    // fake drops them too — otherwise mock-admin would keep showing drafts for
    // candidates that no longer exist, which production cannot do.
    const doomedIds = new Set(doomed.map((c) => c.id));
    for (const draft of MOCK_OUTREACH_DRAFTS.filter((d) => doomedIds.has(d.candidateId))) {
      MOCK_OUTREACH_DRAFTS.splice(MOCK_OUTREACH_DRAFTS.indexOf(draft), 1);
    }
    return doomed.length;
  }

  async updateTestbedCandidate(id: string, input: CandidateUpdateInput): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const stored = MOCK_TESTBED_RUN_CANDIDATES.find((c) => c.id === id);
    if (!stored) throw new Error(`Testbed candidate not found: ${id}`);
    const row = stored.candidate;
    if (input.fullName !== undefined) row.fullName = input.fullName ?? "";
    if (input.email !== undefined) {
      const email = input.email ? input.email.trim() : null;
      if (email && !CANDIDATE_EMAIL_PATTERN.test(email)) {
        throw new Error(`Invalid email address: ${email}`);
      }
      row.email = email;
    }
    const PHONE_PATTERN = /^\+?[\d\s\-().]+$/;
    if (input.phone !== undefined) {
      const phone = input.phone ? input.phone.trim() : null;
      if (phone && !PHONE_PATTERN.test(phone)) {
        throw new Error(`Invalid phone number: ${phone}`);
      }
      row.phone = phone;
    }
    if (input.linkedinUrl !== undefined) row.linkedinUrl = input.linkedinUrl ?? null;
    if (input.employmentTitle !== undefined) row.employmentTitle = input.employmentTitle ?? null;
    if (input.locationCity !== undefined) row.locationCity = input.locationCity ?? null;
    if (input.skills !== undefined) row.skills = input.skills;
  }

  async clearCandidateAiField(id: string, field: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const row = MOCK_HEADHUNTER_CANDIDATES.find((c) => c.id === id);
    if (!row) throw new Error(`Candidate not found: ${id}`);
    row.aiEnrichedFields = row.aiEnrichedFields.filter((f) => f !== field);
  }

  async getHeadhunterHealth(chainId: number): Promise<HeadhunterHealth | null> {
    await delay(MOCK_DELAY_MS);
    // Amoy has a live worker; Polygon (prod) has none reporting yet.
    if (chainId !== 80002) return null;
    return {
      status: "up",
      chainId,
      walletBalanceUsdc: 4.62,
      dailySpendUsdc: 0.84,
      dailyCapUsdc: 5,
      version: "mock-0.1.0",
      lastError: null,
      lastSeenAt: new Date(Date.now() - 45_000).toISOString(),
      updatedAt: new Date(Date.now() - 45_000).toISOString(),
    };
  }

  async listHeadhunterRuns(
    filters: HeadhunterRunFilters,
  ): Promise<PaginatedResult<HeadhunterRun>> {
    await delay(MOCK_DELAY_MS);
    let rows = MOCK_HEADHUNTER_RUNS;
    if (filters.chainId != null) {
      rows = rows.filter((r) => r.chainId === filters.chainId);
    }
    if (filters.status && filters.status !== "all") {
      rows = rows.filter((r) => r.status === filters.status);
    }
    if (filters.kind && filters.kind !== "all") {
      rows = rows.filter((r) => r.kind === filters.kind);
    }
    return { data: rows, total: rows.length, cursor: null, hasMore: false };
  }

  /** DEV-562: mirrors the Supabase adapter — one run's candidates in funnel order. */
  async listTestbedRunCandidates(
    filters: TestbedCandidateFilters,
  ): Promise<PaginatedResult<StoredTestbedCandidate>> {
    await delay(MOCK_DELAY_MS);
    const rows = MOCK_TESTBED_RUN_CANDIDATES.filter((c) => c.runId === filters.runId)
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((c) => ({
        ...c,
        candidate: { ...c.candidate, id: c.id },
      }));
    return { data: rows, total: rows.length, cursor: null, hasMore: false };
  }

  async listHeadhunterLogEvents(
    filters: HeadhunterLogFilters,
  ): Promise<PaginatedResult<HeadhunterLogEvent>> {
    await delay(MOCK_DELAY_MS);
    const drillDown = filters.runId != null;
    let rows = MOCK_HEADHUNTER_LOG_EVENTS.filter(
      (e) => !drillDown || e.runId === filters.runId,
    );
    if (filters.chainId != null) {
      rows = rows.filter((e) => e.chainId === filters.chainId);
    }
    if (filters.level && filters.level !== "all") {
      rows = rows.filter((e) => e.level === filters.level);
    }
    // DEV-535: mirrors the Supabase adapter — filters the event's OWN kind, so
    // an in-flight run's events stay visible. "all" applies no predicate.
    if (filters.kind && filters.kind !== "all") {
      rows = rows.filter((e) => e.kind === filters.kind);
    }
    // Drill-down = chronological (oldest first); tail = newest first.
    rows = [...rows].sort((a, b) =>
      drillDown ? a.createdAt.localeCompare(b.createdAt) : b.createdAt.localeCompare(a.createdAt),
    );
    return { data: rows, total: rows.length, cursor: null, hasMore: false };
  }

  // Note: mutations below modify the module-level MOCK_HEADHUNTER_CANDIDATES
  // array in-place (same pattern as setCandidateStatus). Vite HMR re-evaluates
  // the module on hot reload, silently reverting these mutations to their initial
  // fixture values. This is acceptable for manual QA — the mock is not persistent.
  async markLinkedinSent(candidateId: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const c = MOCK_HEADHUNTER_CANDIDATES.find((x) => x.id === candidateId);
    if (c) {
      c.linkedinOutreachStatus = "sent";
      c.linkedinSentAt = new Date().toISOString();
    }
  }

  async markLinkedinOptedOut(candidateId: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    const c = MOCK_HEADHUNTER_CANDIDATES.find((x) => x.id === candidateId);
    if (c) c.linkedinOutreachStatus = "opted_out";
  }

  async listPendingQuests(
    filters: PendingQuestFilters,
  ): Promise<PaginatedResult<PendingQuest>> {
    await delay(MOCK_DELAY_MS);
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.offset ?? 0;

    // Derive pending quests from mock candidates.
    const byQuest = new Map<string, PendingQuest>();
    for (const c of MOCK_HEADHUNTER_CANDIDATES) {
      if (filters.chainId != null && mockChainForQuest(c.questId) !== filters.chainId) {
        continue;
      }
      const existing = byQuest.get(c.questId);
      if (existing) {
        if (c.source === "internal") existing.internalCount++;
        else existing.externalCount++;
        if (c.status === "invited") existing.invitedCount++;
        if (c.status === "suggested" || c.status === "reviewed") existing.pendingCount++;
        if (c.status === "dismissed") existing.dismissedCount++;
      } else {
        // Alternate status so QA exercises both Badge variants.
        const questStatus = byQuest.size % 2 === 0 ? "published" : "negotiating";
        byQuest.set(c.questId, {
          questId: c.questId,
          title: c.questTitle ?? "Untitled Quest",
          status: questStatus,
          createdAt: c.createdAt ?? new Date().toISOString(),
          internalCount: c.source === "internal" ? 1 : 0,
          externalCount: c.source === "external" ? 1 : 0,
          invitedCount: c.status === "invited" ? 1 : 0,
          pendingCount: c.status === "suggested" || c.status === "reviewed" ? 1 : 0,
          dismissedCount: c.status === "dismissed" ? 1 : 0,
        });
      }
    }

    let items = Array.from(byQuest.values());
    if (filters.search) {
      const q = filters.search.toLowerCase();
      items = items.filter((i) => i.title.toLowerCase().includes(q));
    }
    items.sort((a, b) =>
      (b.internalCount + b.externalCount) - (a.internalCount + a.externalCount),
    );

    const page = items.slice(offset, offset + limit);
    return {
      data: page,
      total: items.length,
      cursor: null,
      hasMore: offset + page.length < items.length,
    };
  }

  async getHeadhunterRunStats(
    chainId: number,
    kind: HeadhunterRunKind | "all" = "quest",
  ): Promise<HeadhunterRunStats> {
    await delay(MOCK_DELAY_MS);
    // Scoped to one network, mirroring the Supabase adapter — totals never blend
    // testnet (Amoy) and mainnet (Polygon) runs.
    const rows = MOCK_HEADHUNTER_RUNS
      .filter((r) => r.chainId === chainId)
      .filter((r) => kind === "all" || r.kind === kind);
    const started = rows.map((r) => r.startedAt).sort();
    return {
      totalRuns: rows.length,
      totalSpendUsdc: rows.reduce((sum, r) => sum + r.spendUsdc, 0),
      totalInternalFound: rows.reduce((sum, r) => sum + r.internalFound, 0),
      totalExternalFound: rows.reduce((sum, r) => sum + r.externalFound, 0),
      totalContactableFound: rows.reduce((sum, r) => sum + (r.contactableFound ?? 0), 0),
      totalCandidatesPersisted: rows.reduce((sum, r) => sum + r.candidatesPersisted, 0),
      firstRunAt: started[0] ?? null,
      lastRunAt: started[started.length - 1] ?? null,
    };
  }

  // ─── Headhunter analytics (DEV-363) ─────────────────────
  // Deterministic fixtures — fixed literal values (no Date.now()/Math.random)
  // so the analytics panels render stable numbers in mock-admin mode.
  async getHeadhunterRunMetrics(
    _filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterRunMetrics> {
    await delay(MOCK_DELAY_MS);
    return {
      totalRuns: 8,
      okRuns: 5,
      partialRuns: 2,
      errorRuns: 1,
      skippedRuns: 0,
      successRate: 0.875,
      totalSpendUsdc: 4.2,
      avgSpendUsdc: 0.525,
      avgDurationMs: 12000,
      thinQuestRuns: 2,
      totalInternalFound: 10,
      totalExternalFound: 6,
      totalCandidatesPersisted: 5,
      costPerPersisted: 0.84,
      firstRunAt: "2026-06-28T09:00:00.000Z",
      lastRunAt: "2026-07-05T17:30:00.000Z",
    };
  }

  async getHeadhunterCandidateFunnel(
    _filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterCandidateFunnel> {
    await delay(MOCK_DELAY_MS);
    return {
      suggested: 20,
      reviewed: 12,
      invited: 8,
      dismissed: 4,
      convertedCount: 3,
      conversionRate: 0.375,
      dismissRate: 0.2,
      outreachSentCount: 8,
      scoreHistogram: [
        { bucket: "1", internal: 0, external: 1 },
        { bucket: "2", internal: 0, external: 1 },
        { bucket: "3", internal: 1, external: 2 },
        { bucket: "4", internal: 1, external: 2 },
        { bucket: "5", internal: 2, external: 3 },
        { bucket: "6", internal: 2, external: 3 },
        { bucket: "7", internal: 3, external: 2 },
        { bucket: "8", internal: 2, external: 2 },
        { bucket: "9", internal: 2, external: 1 },
        { bucket: "10", internal: 1, external: 1 },
        { bucket: "11", internal: 1, external: 0 },
        { bucket: "12", internal: 0, external: 0 },
      ],
      scoreMedianExternal: 0.62,
    };
  }

  async getHeadhunterDropBreakdown(
    _filters: HeadhunterMetricsFilters,
  ): Promise<HeadhunterDropBreakdown> {
    await delay(MOCK_DELAY_MS);
    return {
      stages: [
        { key: "funnel_external_raw", before: 100, after: 100, dropped: 0 },
        { key: "funnel_geofence", before: 100, after: 72, dropped: 28 },
        { key: "funnel_location_filter", before: 72, after: 60, dropped: 12 },
        { key: "funnel_suppression", before: 60, after: 52, dropped: 8 },
        { key: "funnel_skill_gate", before: 52, after: 30, dropped: 22 },
        { key: "funnel_volume_cap", before: 30, after: 20, dropped: 10 },
      ],
      enrichFailures: 3,
    };
  }
}

const MOCK_HEADHUNTER_CANDIDATES: AdminQuestCandidate[] = [
  {
    id: "mock-cand-int-1",
    questId: "mock-quest-001",
    questTitle: "Build a landing page for the launch",
    source: "internal",
    memberId: "did:privy:mock-ada",
    convertedUserId: null,
    fullName: "Ada Benítez",
    locationCity: "Asunción",
    locationCountry: "Paraguay",
    skills: ["React", "TypeScript", "Node.js"],
    employmentTitle: "Frontend Developer",
    linkedinUrl: null,
    // DEV-807: an internal row NEVER carries sourced contact — the worker
    // hardcodes email/phone null for members (internalSearch.ts toCandidate) and
    // never reads a contact channel into quest_candidates. The address below is
    // the member's OWN connected channel, resolved live by the RPC.
    email: null,
    phone: null,
    emailVerified: null,
    score: 0.95,
    status: "suggested",
    outreachSentAt: null,
    sourceUrl: null,
    collectedAt: daysAgo(1),
    lawfulBasis: "consent",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(1),
    createdAt: daysAgo(1),
    linkedinOutreachStatus: "none",
    linkedinSentAt: null,
    dismissalReason: null,
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    candidateIdentityId: null,
    otherQuestCount: 0,
    sourcePlatform: null,
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    skillsFromQuery: false,
    locationFromQuery: false,
    memberEmail: "ada@wexare.com",
    // DEV-822: NOT seeded — resolveCandidateLeadState resolves it per read in
    // listHeadhunterCandidates, mirroring the RPC's LATERAL. A seeded value
    // would go stale the moment a lead is created and defeat the whole point.
    leadId: null,
    leadClaimedAt: null,
    manuallyAdded: false,
    referral: null,
    // A past volunteer, so the Staffing badge and "Invite to volunteer" can be
    // seen in mock mode (VITE_MOCK_ROLE=admin). Also asked for more (20260923120000).
    volunteerHistory: { tasksCompleted: 3, lastCompletedAt: "2026-08-14T00:00:00Z", sameCreator: true, sharedSkills: ["design"], askedForVolunteer: true },
  },
  {
    id: "mock-cand-ext-1",
    questId: "mock-quest-001",
    questTitle: "Build a landing page for the launch",
    source: "external",
    memberId: null,
    convertedUserId: null,
    fullName: "Carlos Cardozo",
    locationCity: "Asunción",
    locationCountry: "Paraguay",
    skills: ["Software Engineering", "React", "Next.js"],
    employmentTitle: "Co-Founder",
    linkedinUrl: "https://www.linkedin.com/in/carloscardozov",
    email: "carlos@example.com",
    phone: "+595 981 000111",
    emailVerified: true,
    score: 0.82,
    status: "invited",
    outreachSentAt: daysAgo(2),
    sourceUrl: "fullenrich/people-search",
    collectedAt: daysAgo(1),
    lawfulBasis: "legitimate_interest",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(1),
    createdAt: daysAgo(1),
    linkedinOutreachStatus: "sent",
    linkedinSentAt: daysAgo(1),
    dismissalReason: null,
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    // DEV-450: also sourced for another quest (mock-quest-002) — demonstrates
    // the "+N other quests" badge/expansion in mock-admin mode.
    candidateIdentityId: "mock-identity-carlos",
    otherQuestCount: 1,
    sourcePlatform: "worker_referral",
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    skillsFromQuery: false,
    locationFromQuery: false,
    memberEmail: null,
    leadId: null,
    leadClaimedAt: null,
    // Referred by a member through the quest's Refer form, so the "Referred
    // by" badge, the note and the copy-link can be seen in mock mode.
    manuallyAdded: true,
    referral: {
      referredByUserId: "did:privy:mock-worker",
      referredByName: "Ana Benítez",
      code: "ABCDEFGHJK",
      note: "My cousin — does plumbing on weekends, very reliable.",
    },
    volunteerHistory: null,
  },
  {
    id: "mock-cand-ext-2",
    questId: "mock-quest-002",
    questTitle: "Design a logo and brand kit",
    source: "external",
    memberId: null,
    convertedUserId: null,
    fullName: "María Gómez",
    locationCity: "Buenos Aires",
    locationCountry: "Argentina",
    skills: ["Brand Design", "Illustration"],
    employmentTitle: "Senior Designer",
    linkedinUrl: "https://www.linkedin.com/in/mariagomez",
    email: null,
    phone: null,
    emailVerified: null,
    score: 0.71,
    status: "suggested",
    outreachSentAt: null,
    sourceUrl: "fullenrich/people-search",
    collectedAt: daysAgo(3),
    lawfulBasis: "legitimate_interest",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(3),
    createdAt: daysAgo(3),
    linkedinOutreachStatus: "opted_out",
    linkedinSentAt: null,
    dismissalReason: null,
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    candidateIdentityId: null,
    otherQuestCount: 0,
    sourcePlatform: null,
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    // DEV-503: the ONE echoed fixture. Every other mock candidate is attested,
    // so mock-admin mode exercises both the labelled and the plain rendering —
    // and a regression that demotes real evidence still shows up next to it.
    skillsFromQuery: true,
    locationFromQuery: true,
    memberEmail: null,
    leadId: null,
    leadClaimedAt: null,
    manuallyAdded: false,
    referral: null,
    volunteerHistory: null,
  },
  {
    // Dismissed external prospect: an admin explicitly ruled this person out,
    // so the card/drawer must NOT offer a "Send to outreach review" CTA and the
    // worker must never draft for them. Exercises the dismissed-status guard.
    id: "mock-cand-ext-3",
    questId: "mock-quest-001",
    questTitle: "Build a landing page for the launch",
    source: "external",
    memberId: null,
    convertedUserId: null,
    fullName: "Diego Ramírez",
    locationCity: "Encarnación",
    locationCountry: "Paraguay",
    skills: ["React", "Tailwind"],
    employmentTitle: "Web Developer",
    linkedinUrl: "https://www.linkedin.com/in/diegoramirez",
    email: "diego@example.com",
    phone: null,
    emailVerified: true,
    score: 0.68,
    status: "dismissed",
    outreachSentAt: null,
    sourceUrl: "fullenrich/people-search",
    collectedAt: daysAgo(2),
    lawfulBasis: "legitimate_interest",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(2),
    createdAt: daysAgo(2),
    linkedinOutreachStatus: "none",
    linkedinSentAt: null,
    dismissalReason: "skills_mismatch",
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    candidateIdentityId: null,
    otherQuestCount: 0,
    sourcePlatform: null,
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    skillsFromQuery: false,
    locationFromQuery: false,
    memberEmail: null,
    leadId: null,
    leadClaimedAt: null,
    manuallyAdded: false,
    referral: null,
    volunteerHistory: null,
  },
  {
    // Medium-tier internal (score in [MIN_MATCH_SCORE, MATCH_TIER_MEDIUM)) — W-B
    // scores internals too, so the tier filter/grouping has a non-high internal
    // to bucket in mock-admin. Amoy (mock-quest-001).
    id: "mock-cand-int-2",
    questId: "mock-quest-001",
    questTitle: "Build a landing page for the launch",
    source: "internal",
    memberId: "did:privy:mock-lucia",
    convertedUserId: null,
    fullName: "Lucía Franco",
    locationCity: "Lima",
    locationCountry: "Peru",
    skills: ["Figma", "UI Design"],
    employmentTitle: "Product Designer",
    linkedinUrl: null,
    // DEV-807: an internal row NEVER carries sourced contact — the worker
    // hardcodes email/phone null for members (internalSearch.ts toCandidate) and
    // never reads a contact channel into quest_candidates. The address below is
    // the member's OWN connected channel, resolved live by the RPC.
    email: null,
    phone: null,
    emailVerified: null,
    score: 0.48,
    status: "suggested",
    outreachSentAt: null,
    sourceUrl: null,
    collectedAt: daysAgo(4),
    lawfulBasis: "consent",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(4),
    createdAt: daysAgo(4),
    linkedinOutreachStatus: "none",
    linkedinSentAt: null,
    dismissalReason: null,
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    candidateIdentityId: null,
    otherQuestCount: 0,
    sourcePlatform: null,
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    skillsFromQuery: false,
    locationFromQuery: false,
    memberEmail: "lucia@wexare.com",
    leadId: null,
    leadClaimedAt: null,
    manuallyAdded: false,
    referral: null,
    volunteerHistory: null,
  },
  {
    // Low-tier external (score in [0, MIN_MATCH_SCORE)) — a weak match so the
    // "low" tier filter has a member. Polygon (mock-quest-002).
    id: "mock-cand-ext-4",
    questId: "mock-quest-002",
    questTitle: "Design a logo and brand kit",
    source: "external",
    memberId: null,
    convertedUserId: null,
    fullName: "Bruno Silva",
    locationCity: "Montevideo",
    locationCountry: "Uruguay",
    skills: ["Copywriting"],
    employmentTitle: "Marketing Assistant",
    linkedinUrl: "https://www.linkedin.com/in/brunosilva",
    email: "bruno@example.com",
    phone: null,
    emailVerified: false,
    score: 0.22,
    status: "suggested",
    outreachSentAt: null,
    sourceUrl: "fullenrich/people-search",
    collectedAt: daysAgo(5),
    lawfulBasis: "legitimate_interest",
    purpose: "quest_staffing",
    lastRefreshedAt: daysAgo(5),
    createdAt: daysAgo(5),
    linkedinOutreachStatus: "none",
    linkedinSentAt: null,
    dismissalReason: null,
    dismissalNote: null,
    sourceUrls: [],
    aiProfile: null,
    aiProfileGeneratedAt: null,
    aiAssessment: null,
    aiAssessmentGeneratedAt: null,
    resumeUrl: null,
    aiEnrichedFields: [],
    candidateIdentityId: null,
    otherQuestCount: 0,
    sourcePlatform: null,
    socialProfiles: null,
    entityType: "person" as const,
    mapsPlaceId: null,
    formattedAddress: null,
    mapsUri: null,
    skillsFromQuery: false,
    locationFromQuery: false,
    memberEmail: null,
    leadId: null,
    leadClaimedAt: null,
    manuallyAdded: false,
    referral: null,
    volunteerHistory: null,
  },
];

// DEV-450: other-quest fixture data, keyed by candidateIdentityId, for
// getOtherQuestsForIdentity. Only identities with otherQuestCount > 0 above
// need an entry here.
const MOCK_OTHER_QUESTS: Record<string, OtherQuestMatch[]> = {
  "mock-identity-carlos": [
    { questId: "mock-quest-002", questTitle: "Ship the mobile app redesign", status: "suggested" },
  ],
};

const MOCK_HEADHUNTER_RUNS: HeadhunterRun[] = [
  {
    id: "mock-run-1",
    chainId: 80002,
    questId: "mock-quest-001",
    questTitle: "Build a landing page for the launch",
    status: "ok",
    kind: "quest",
    skippedReason: null,
    internalFound: 1,
    externalFound: 2,
    contactableFound: 2,
    candidatesPersisted: 3,
    spendUsdc: 0.42,
    walletBalanceAfter: 4.62,
    durationMs: 8200,
    errorMessage: null,
    startedAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
    finishedAt: new Date(Date.now() - 2 * 3600_000 + 8200).toISOString(),
    workContext: null,
    queryLabel: null,
    spendBasis: "settled",
  },
  {
    id: "mock-run-2",
    chainId: 80002,
    questId: "mock-quest-003",
    questTitle: "Translate docs to Spanish",
    status: "skipped",
    kind: "quest",
    skippedReason: "no skills",
    internalFound: 0,
    externalFound: 0,
    contactableFound: 0,
    candidatesPersisted: 0,
    spendUsdc: 0,
    walletBalanceAfter: 5.04,
    durationMs: 120,
    errorMessage: null,
    startedAt: new Date(Date.now() - 5 * 3600_000).toISOString(),
    finishedAt: new Date(Date.now() - 5 * 3600_000 + 120).toISOString(),
    workContext: null,
    queryLabel: null,
    spendBasis: "estimated",
  },
  {
    id: "mock-run-3",
    chainId: 137,
    questId: "mock-quest-002",
    questTitle: "Design a logo and brand kit",
    status: "error",
    kind: "quest",
    skippedReason: null,
    internalFound: 0,
    externalFound: 1,
    contactableFound: 0,
    candidatesPersisted: 0,
    spendUsdc: 0.2,
    walletBalanceAfter: 5.04,
    durationMs: 4100,
    errorMessage: "Settlement failed after 3 retries (pdl/people-enrich)",
    startedAt: new Date(Date.now() - 26 * 3600_000).toISOString(),
    finishedAt: new Date(Date.now() - 26 * 3600_000 + 4100).toISOString(),
    workContext: null,
    queryLabel: null,
    spendBasis: "mixed",
  },
  {
    id: "mock-run-4",
    chainId: 80002,
    questId: null,
    questTitle: null,
    status: "ok",
    kind: "testbed",
    skippedReason: null,
    internalFound: 0,
    externalFound: 5,
    contactableFound: 2,
    // DEV-562: a testbed run now keeps what it found, so this matches
    // externalFound rather than the old hard 0.
    candidatesPersisted: 5,
    spendUsdc: 0.9,
    walletBalanceAfter: 3.72,
    durationMs: 6400,
    errorMessage: null,
    startedAt: new Date(Date.now() - 1 * 3600_000).toISOString(),
    finishedAt: new Date(Date.now() - 1 * 3600_000 + 6400).toISOString(),
    workContext: "local_business",
    queryLabel: "titles: Plumber · skills: plumbing · in Asunción",
    spendBasis: null,
  },
];

// DEV-562: stored candidates for the testbed run (mock-run-4), so mock-admin
// mode can exercise the run-log drill-down. Deliberately mixed: a reachable
// business from the Maps leg, a person with an email, and contactless
// web-fallback hits — the drill-down's job is to show that the difference is
// visible, so a fixture where everything has a phone would prove nothing.
const MOCK_TESTBED_RUN_CANDIDATES: StoredTestbedCandidate[] = [
  {
    id: "mock-tbc-1",
    runId: "mock-run-4",
    ordinal: 0,
    candidate: {
      source: "external",
      entityType: "business",
      fullName: "Plomería García",
      skills: [],
      phone: "+595 21 555 0101",
      formattedAddress: "Av. España 1234, Asunción",
      sourceLeg: "maps",
      mapsPlaceId: "mock-place-1",
      score: null,
    },
  },
  {
    id: "mock-tbc-2",
    runId: "mock-run-4",
    ordinal: 1,
    candidate: {
      source: "external",
      entityType: "business",
      fullName: "Destapaciones 24h Asunción",
      skills: [],
      phone: "+595 21 555 0187",
      formattedAddress: "Mcal. López 890, Asunción",
      sourceLeg: "maps",
      mapsPlaceId: "mock-place-2",
      score: null,
    },
  },
  {
    id: "mock-tbc-3",
    runId: "mock-run-4",
    ordinal: 2,
    candidate: {
      source: "external",
      entityType: "person",
      fullName: "Ramón Ortega",
      locationCity: "Asunción",
      locationCountry: "PY",
      skills: ["plumbing", "pipe fitting"],
      employmentTitle: "Plumber",
      email: "ramon.ortega@example.com",
      emailVerified: true,
      sourceLeg: "people_search",
      score: null,
    },
  },
  {
    id: "mock-tbc-4",
    runId: "mock-run-4",
    ordinal: 3,
    candidate: {
      source: "external",
      entityType: "person",
      fullName: "Lucía Benítez",
      locationCity: "Asunción",
      locationCountry: "PY",
      skills: ["plumbing"],
      linkedinUrl: "https://www.linkedin.com/in/mock-lucia-benitez",
      sourceLeg: "web_fallback",
      score: null,
    },
  },
  {
    id: "mock-tbc-5",
    runId: "mock-run-4",
    ordinal: 4,
    candidate: {
      source: "external",
      entityType: "person",
      fullName: "Sanitarios del Este",
      skills: [],
      sourceLeg: "web_fallback",
      score: null,
    },
  },
];

// Step-level log fixtures — enough to exercise run drill-down (mock-run-1) and
// the live tail (incl. a run_id=null worker_started event). Sanitized shape.
const _hhLogBase = Date.now() - 2 * 3600_000; // aligns with mock-run-1's startedAt
const MOCK_HEADHUNTER_LOG_EVENTS: HeadhunterLogEvent[] = [
  {
    kind: "quest" as const,
    id: "mock-log-0",
    chainId: 80002,
    runId: null,
    event: "worker_started",
    level: "info",
    message: "worker_started",
    fields: { version: "mock-0.1.0" },
    createdAt: new Date(_hhLogBase - 60_000).toISOString(),
  },
  {
    kind: "quest" as const,
    id: "mock-log-1",
    chainId: 80002,
    runId: "mock-run-1",
    event: "run_started",
    level: "info",
    message: "run_started",
    fields: { questId: "mock-quest-001" },
    createdAt: new Date(_hhLogBase).toISOString(),
  },
  {
    kind: "quest" as const,
    id: "mock-log-2",
    chainId: 80002,
    runId: "mock-run-1",
    event: "funnel_internal",
    level: "info",
    message: "funnel_internal",
    fields: { questId: "mock-quest-001", found: 1, threshold: 3, skills: ["React", "Node"] },
    createdAt: new Date(_hhLogBase + 500).toISOString(),
  },
  {
    kind: "quest" as const,
    id: "mock-log-3",
    chainId: 80002,
    runId: "mock-run-1",
    event: "funnel_geofence",
    level: "info",
    message: "funnel_geofence",
    fields: { before: 10, after: 6, droppedCountries: ["BR", "GB"] },
    createdAt: new Date(_hhLogBase + 1200).toISOString(),
  },
  {
    kind: "quest" as const,
    id: "mock-log-4",
    chainId: 80002,
    runId: "mock-run-1",
    event: "enrich_candidate_failed",
    level: "error",
    message: "enrich_candidate_failed",
    fields: { error: "settlement timeout" },
    createdAt: new Date(_hhLogBase + 3400).toISOString(),
  },
  {
    // DEV-535: a testbed event whose run row was never written (the run is
    // still in flight). It must be visible under Testbed and All runs, and
    // absent from Quests — the case a join to headhunter_runs would lose.
    kind: "testbed" as const,
    id: "mock-log-5",
    chainId: 80002,
    runId: "mock-run-testbed-inflight",
    event: "search_test_leg",
    level: "info",
    message: "search_test_leg",
    fields: { step: "web", vendor: "clado", totalFound: 4, contactable: 1 },
    createdAt: new Date(_hhLogBase + 4200).toISOString(),
  },
];
